import {
  Home, Users, Calendar, ClipboardList, Activity, FileText, Search,
  FileSearch, LayoutGrid, DollarSign, UserCog, AlertTriangle, Shield,
  ChevronRight, Monitor, Settings, Printer, BookOpen, Wrench, Clock, Route
} from 'lucide-react';

export const modules = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    subtitle: 'Karisma Environment & Navigation',
    icon: 'Home',
    color: '#DB2777',
    description: 'Learn the Karisma K4 interface, navigation, menu system, universal keyboard shortcuts, and how to set up your workspace.',
    steps: [
      {
        title: 'The Karisma Home Page',
        content: `The Home Page is your starting point in Karisma K4. It provides quick access to all modules and features.

**Key Elements:**
- **Navigation Bar** — Located at the top, provides global functions: Back, Forward, Refresh, Home, Menu, Find, and Notifications buttons.
- **Tab Bar** — Allows multiple screens to be opened simultaneously. Each open module displays as a numbered tab along the bottom of the window. The active tab is highlighted.
- **Favourites** — Frequently accessed screens can be pinned as "Startup" (opens automatically on login) or "Pinned" (always visible in the navigation panel).`
      },
      {
        title: 'The Navigation Bar',
        content: `The Navigation Bar sits at the top of every screen and provides essential navigation tools.

**Buttons available:**
- **Back / Forward** — Navigate between recently viewed screens, similar to a web browser.
- **Refresh** — Reload the current screen data.
- **Home** — Return to the Karisma Home Page.
- **Menu** — Open the main Menu screen (also accessible via Ctrl+G).
- **Find** — Spotlight search that finds content across all of Karisma.
- **Notifications** — View system alerts and messages.`
      },
      {
        title: 'The Menu Screen',
        content: `Access the Menu Screen via the Menu button or Ctrl+G. This is your gateway to all Karisma modules.

**Routine Workflow Screens:**
- **Diary** — Scheduling and booking management
- **Reception** — Patient registration and check-in
- **Imaging** — Service management and workflow
- **Reporting** — Report creation, dictation, and distribution
- **Accounting** — Invoice and payment management

**System Administration:**
- **Data Management** — Configure system settings, templates, and rules
- **Security Directory** — Manage users, groups, roles, and permissions

From each module link, you can open or pin it to the Navigation Panel as a Favourite.`
      },
      {
        title: 'Customise and Control Karisma',
        content: `Click the gear icon (cog) to the right of the Tab Bar to access configuration settings.

**Available Options:**
- **Reload this screen** — Refresh the current view
- **Apply dark theme** — Toggle between light and dark mode
- **Toggle display mode** — Switch between compact and expanded view
- **Toggle full screen mode** — Maximise the application window
- **Copy URL to clipboard** — Share the current screen location
- **Send URL in email** — Email the current screen link
- **Switch User** — Change to a different user account
- **New window** — Open a new Karisma window
- **Reset profile settings** — Restore default profile configuration
- **Reset screen settings** — Restore default screen layout
- **Flush security content** — Clear cached security data
- **Launch web application** — Open the web version
- **Restart / Exit this application** — Restart or close Karisma`
      },
      {
        title: 'Universal Keyboard Shortcuts',
        content: `These shortcuts work throughout Karisma and save significant time.

**Commonly Used Keys:**
| Key | Function |
|-----|----------|
| **Ins** | Insert / Add a new record |
| **Del** | Delete the selected record |
| **F2** | Edit the selected record |
| **F4** | Open dropdown / selection list |
| **F5** | Refresh the current screen |
| **Ctrl+G** | Open the Menu screen |
| **Ctrl+Space** | Auto-complete (e.g., in report templates) |
| **Tab** | Move to the next input field |

**Tip:** These shortcuts are consistent across all modules, so learning them once applies everywhere.`
      },
      {
        title: 'Sorting and Filtering Records',
        content: `When working with large datasets, use sorting and filtering to find specific records quickly.

**Sorting:**
- Click any column header to sort records alphabetically or chronologically.
- Click again to reverse the sort order.

**Filtering:**
- Use the filter options at the top of grids to restrict displayed data.
- Select specific criteria to narrow results.

**Managing Filters:**
- **Create** a new custom filter
- **Edit** an existing filter
- **Delete** a filter you no longer need
- Filters can be saved for reuse across sessions.`
      },
      {
        title: 'Workstation & Worksite Configuration',
        content: `Key users need to configure workstations to ensure users connect to the correct worksite.

**Steps to configure:**
1. Navigate to **Data Management**
2. Search for **"Workstations"**
3. Select the specific workstation (e.g., "Charisma vendor 504")
4. Edit the workstation settings:
   - Enable **"Always ask for work site on login"** for key users who need to switch between sites
   - Set a **"Default work site"** for users who always work at one location

**Identifying Your Workstation:**
Look at the "signed in as" information in the top right corner, next to the gear (cog) icon.

**Training End-Users:**
Instruct them to pick their worksite and select **"Remember selected work site"** to avoid repeated prompts. Key users should generally NOT select this option to maintain flexibility.`
      }
    ]
  },
  {
    id: 'patient-module',
    title: 'Patient Module',
    subtitle: 'Demographics & Patient Management',
    icon: 'Users',
    color: '#7C3AED',
    description: 'Manage patient records including demographics, identifiers, addresses, allergies, conditions, and next of kin.',
    steps: [
      {
        title: 'Patient Module Overview',
        content: `The Patient Module facilitates management of all patient-related data and requirements.

**Key Features:**
- Patient search across multiple criteria
- Demographic information management
- Handling duplicate patient records
- Patient Master Index interfacing
- Recording minimal demographics for quick registration
- Privacy management and patient conditions
- Allergy and condition tracking`
      },
      {
        title: 'Finding a Patient',
        content: `You can locate existing patient records from the Reception, Imaging, or Practice Management screens.

**Search Methods:**
- **By Name** — Enter the patient's surname, first name, or partial name
- **By Date of Birth** — Search using DOB in DD/MM/YYYY format
- **By Identifier** — Use Medicare number, MRN, or other configured identifiers

**Tips:**
- Use partial searches (first few letters) for faster results
- Combine criteria for more precise matching
- The system will display matching results in a list for selection`
      },
      {
        title: 'Patient Details Dialog',
        content: `The Patient Details Dialog stores all patient information organised under clear headings.

**Sections:**
- **Name** — Title, surname, first name, middle name, preferred name
- **Demographic** — Date of birth, sex, indigenous status, interpreter required
- **Identifier** — Medicare number, MRN, DVA number, other custom identifiers
- **Contact** — Phone numbers (home, work, mobile), email address
- **Address** — Current, home, mailing, work, and other address types
- **Notes** — Free text notes about the patient
- **Health Fund** — Private health insurance details
- **Allergy** — Recorded patient allergies (displayed as a pill icon in worklists)
- **Condition** — Patient conditions like claustrophobia (displayed as a triangle icon)
- **Next of Kin** — Emergency contact information`
      },
      {
        title: 'Patient Rules',
        content: `Patient Rules define requirements for uniform patient data entry. These rules can be adapted for different workflow stages.

**Configuration:**
- Rules dictate which fields are displayed, mandatory, or optional
- Different rules can apply at different stages (e.g., registration vs. imaging)
- Configure via **Data Management > Patient Rules**

**Examples of configurable rules:**
- Make Medicare number mandatory at registration
- Make phone number mandatory
- Display interpreter required field
- Require date of birth verification`
      },
      {
        title: 'Patient Identifiers',
        content: `System administrators can define various patient identifier types to support different workflows.

**Common Identifier Types:**
- **MRN** (Medical Record Number) — Hospital/clinic unique identifier
- **Medicare** — Australian Medicare card number
- **DVA** — Department of Veterans' Affairs number
- **Health Fund** — Private insurance member number

**Value Masks:**
Identifiers can have format masks to enforce correct data entry structure (e.g., Medicare numbers must follow XXXX XXXXX X format).

**Using Identifiers:**
- Define rules for displaying identifiers
- Set which identifiers are mandatory
- Enable search by specific identifier types`
      },
      {
        title: 'Patient Addresses',
        content: `Karisma supports recording multiple address types for each patient.

**Address Types:**
- **Current** — Where the patient currently resides
- **Home** — Permanent home address
- **Legal** — Legal/official address
- **Mailing** — Postal address for correspondence
- **Permanent** — Long-term address
- **Work** — Workplace address

Each address can include street, suburb, state, postcode, and country fields.`
      },
      {
        title: 'Patient Conditions',
        content: `Patient conditions can be assigned to tailor service delivery and alert staff.

**Common Condition Types:**
- Claustrophobic
- Infectious
- Aggressive/Violent
- Requires wheelchair
- Hearing impaired
- Vision impaired

**How conditions appear:**
- A triangle icon appears next to the patient name in worklists
- Staff can hover over the icon to see condition details
- Conditions help staff prepare appropriately for patient interactions

**Configuration:**
Navigate to **Data Management > Patient Condition Types** to create or manage condition types.`
      }
    ]
  },
  {
    id: 'scheduling',
    title: 'Scheduling & Diary',
    subtitle: 'Resource Management & Bookings',
    icon: 'Calendar',
    color: '#2563EB',
    description: 'Manage the scheduling diary, resources, operating profiles, resource conditions, and patient bookings.',
    steps: [
      {
        title: 'Scheduling Overview',
        content: `The Scheduling module allocates resources for efficient service completion.

**Benefits of scheduling:**
- Efficient resource allocation (rooms, equipment, staff)
- Proper patient preparation (fasting, contrast, etc.)
- Optimised patient turnaround times
- Better workload distribution

**Key Elements to Configure:**
Resources, Resource Types, Resource Views, Resource View Rules, Operating Profiles, Public Holidays, Preparation Notes, Preparation Instructions, Age Brackets, Time Periods, Diary Profiles, Forecast Span Designs, SMS Templates, Appointment Reminder Types.`
      },
      {
        title: 'The Scheduling Diary',
        content: `The Diary is the central booking management tool for your worksite.

**Workflow Tasks and Tools:**
- **Refresh** — Update the diary view
- **Schedule Request** — Create a new booking
- **Apply Resource Condition** — Set availability conditions
- **Distribute Resource Forecast** — Share resource schedules
- **Appointment Sheet** — Print appointment lists

**Other Functions:**
- Date controls — Navigate between days/weeks
- Order columns — Arrange resource display
- Rotate — Switch between horizontal/vertical view
- Visible column count — Control how many resources display
- Zoom — Adjust time slot granularity`
      },
      {
        title: 'Resources & Resource Types',
        content: `Resources are individual entities required for services (rooms, radiologists, nurses, equipment).

**Resource Types** group similar resources into categories (e.g., all CT rooms, all ultrasound technologists).

**Adding a New Resource:**
1. Navigate to **Data Management > Resources**
2. Click **New**
3. Enter the resource details:
   - **Code** — Unique identifier
   - **Name** — Descriptive name
   - **Resource Type** — Select the category
   - **Work Site** — Assign to a location
4. Click **Save**

Resources must be defined in Karisma before they can be used for bookings.`
      },
      {
        title: 'Operating Profiles',
        content: `Operating Profiles specify when resources are available for use (dates and hours).

**Configuration:**
- Set the start and end dates for availability
- Define working hours (e.g., 8:00 AM to 5:00 PM)
- Can be configured for specific days of the week
- Multiple profiles can apply to the same resource for different periods

**Example:**
- CT Room 1: Available Monday-Friday 7:00 AM - 9:00 PM
- CT Room 1: Available Saturday 8:00 AM - 1:00 PM`
      },
      {
        title: 'Resource Conditions',
        content: `Resource Conditions define specific times within availability that constrain resource utilisation.

**Condition Types:**
- **Unavailable** — Resource cannot be booked (e.g., maintenance, meetings)
- **Available** — Resource is specifically available (overrides unavailable periods)
- **Designated** — Resource is reserved for specific purposes (e.g., emergency slots)

**Understanding the Resource Condition Dialog:**
Parameters are classified under three tabs:

1. **General** — Type of condition, colour coding, description
2. **Occurrence Pattern** — One-off or recurring (daily, weekly, monthly)
3. **Parameters** — Constraints based on:
   - Age Brackets
   - Patient Context
   - Service Procedure

**Managing Conditions:**
- Adjust time periods by dragging condition blocks
- Delete conditions that are no longer needed
- Conditions display as coloured blocks in the diary view`
      },
      {
        title: 'Resource Views & Rules',
        content: `Resource Views determine which bookable resources are displayed in the diary for each user.

**Defining Resource Views:**
1. Navigate to **Data Management > Resource Views**
2. Click **New**
3. Select the resources to include
4. Name the view (e.g., "CT Resources", "US Resources")
5. Save

**Resource View Rules:**
Define rules for making views available to appropriate users.
- Rules can be based on user roles, groups, or worksites
- Ensures users only see resources relevant to their work

**Diary Profiles:**
Control the colour scheme and visual presentation of the scheduling diary.
- Work sites can customise their diary appearance
- Configure initial start time, appointment format, colour sources`
      },
      {
        title: 'Public Holidays',
        content: `Define public holidays to manage restricted service availability.

**Steps:**
1. Navigate to **Data Management > Public Holidays**
2. Click **New**
3. Enter the holiday name and date
4. Specify which services are restricted
5. Save

Public holidays affect scheduling by:
- Blocking bookings on specified dates
- Displaying holiday indicators in the diary
- Adjusting resource availability automatically`
      },
      {
        title: 'Communicating with Patients',
        content: `The Scheduling module can communicate service and appointment details to patients via:

- **Preparation Notes** — Instructions about what patients need to do before their appointment (e.g., fasting requirements, contrast preparation)
- **Preparation Instructions** — Detailed instructions for specific procedures
- **Appointment Letters** — Formal appointment confirmation letters
- **SMS** — Text message reminders and confirmations

**SMS Templates:**
Configure standardised SMS messages for appointment reminders, including appointment date, time, location, and preparation instructions.`
      }
    ]
  },
  {
    id: 'reception',
    title: 'Reception',
    subtitle: 'Patient Registration & Check-in',
    icon: 'ClipboardList',
    color: '#059669',
    description: 'Register patients, verify demographics, manage requests, scan referral documents, and handle walk-in patients.',
    steps: [
      {
        title: 'Reception Overview',
        content: `The Reception screen manages patient arrival and registration. This is where front desk staff process patients who arrive for their appointments.

**Key Tasks:**
- Check and update patient details
- Verify Medicare/Veterans' Affairs details
- Register the request (check patient in)
- Print labels for imaging
- Scan referral paperwork
- Handle walk-in patients`
      },
      {
        title: 'Registering a Request',
        content: `When a patient arrives, you need to register their request (check them in).

**Steps:**
1. Open the **Reception** screen from the Menu
2. Find the patient's booking in the worklist
3. Click on the patient tile to select it
4. Verify patient demographics — confirm name, DOB, address, and phone
5. Check the referral document — ensure it matches the booked service
6. Update any changed details (address, phone, Medicare)
7. Select the **Account Type** (Medicare, Private, DVA, etc.)
8. Click **Register** to complete check-in

**Important:**
- Always verify patient identity using at least two identifiers (name + DOB)
- Scan the referral form if required by your worksite's scanning rules
- Print labels if needed for imaging`
      },
      {
        title: 'Request Details Dialog',
        content: `The Request Details Dialog contains all information about the imaging request.

**Tabs Available:**
- **Details** — Service information and request type
- **Identification** — Request identifiers and reference numbers
- **Locations** — Hospital, ward, unit, and context
- **Practitioners and Units** — Requesting, responsible, reporting, and invoicing practitioners
- **Date/Time of Events** — Service dates, admission dates, operation dates
- **Workflow Information** — Current status and workflow stage
- **Special Conditions** — Patient conditions and special requirements
- **Request Details** — Additional request-specific information
- **Account** — Billing and account type information`
      },
      {
        title: 'Request Rules',
        content: `Request Rules configure the options available on the Request Dialog and can differ by workflow stage and worksite.

**Rule Categories:**
- **General** — Naming conventions and disabling rules
- **Accounting** — Last invoicing practitioner, default account type, pricing requirements
- **Practitioner** — Requesting, responsible, and default reporting practitioner options
- **Unit** — Requesting/responsible unit and ward settings
- **Date/Time** — Default service dates, servicing date allowances
- **Priority** — Enable priority selection (Patient Interaction, Service Completion, Report Completion)
- **Related** — External request numbers, referral reasons, clinical/order notes
- **Scanning** — Scanned request forms (optional or mandatory)

Request Rules can be configured differently for each status:
- **Receive** — When order is received
- **Schedule** — When booking is made
- **Register** — When patient arrives`
      },
      {
        title: 'Request Priority Types',
        content: `Karisma supports prioritising work across different categories:

**Priority Categories:**
- **Patient Interaction** — Priority for patient contact
- **Service Completion** — Priority for completing the imaging service
- **Report Completion** — Priority for completing the report
- **Report Delivery** — Priority for distributing the report
- **Transport** — Priority for patient transport

**Customising Priorities:**
- Edit the text for each priority level
- Assign colours for visual identification in worklists
- Mark priorities as "important" for highlighting

**Ordering Worklists:**
Reception and Imaging screens can sort requests by priority, ensuring urgent cases are seen first.`
      },
      {
        title: 'Scanning Documents',
        content: `Karisma supports scanning documents against Patient and Request records.

**Form Types:**
Form Types are defined with either:
- **Patient Scope** — Documents attached to the patient record (e.g., consent forms)
- **Request Scope** — Documents attached to a specific request (e.g., referral forms)

**Scanning Workflow:**
1. Place the document on the scanner
2. In Karisma, navigate to the scanning section
3. Select the Form Type (e.g., "Referral Form")
4. Click **Scan**
5. The scanned image will attach to the patient or request record

**Notes:**
- Your worksite may require mandatory scanning of referral forms before registration
- Scanned documents are viewable from the Request Search and Report Dialog
- Set a default scanner source in workstation configuration`
      },
      {
        title: 'Requesting Practitioners',
        content: `Practitioners (doctors, referrers) must exist in Karisma before they can be linked to requests.

**Three ways to enter practitioners:**
1. **Practitioner Management Screen** — Full administration of practitioner records
2. **Request Dialog** — Quick entry while processing a request
3. **Data Import** — Bulk import from external systems

**Key Fields:**
- Unique practitioner code
- Name and description
- Provider number (Medicare assigns per location)
- Specialty (important for invoicing)
- Contact details and locations

**Practitioner Locations and Assignments:**
A single practitioner record can be associated with multiple locations. Each location may have a different provider number.`
      },
      {
        title: 'Scheduling a Request (Step-by-Step)',
        content: `**From the Reception Workflow Screen:**
1. Open **Reception** from the Main Menu
2. **Search** for your patient and select them
3. Patient and request jackets displayed
4. Click the **Schedule action bar button**
5. Complete the **Schedule Request Details** dialog, click **OK**
6. The **Diary** opens with the next available time slot for the Service Type

**If the time is suitable:** Click **Save**

**If you need a different time that day:**
- Left-click the **green time slot** and **drag** to the desired time
- Click **Save**

**If you need a different day:**
- Click **Release allocations**
- Change the date using the **Calendar buttons**
- Select the desired time slot
- Click **Save**

If appointment letters are in use, a prompt to print will appear. The Reception screen will then display the scheduled request.`
      },
      {
        title: 'Rescheduling a Request (Step-by-Step)',
        content: `**From the Reception Workflow Screen:**
1. Open **Reception** from the Main Menu
2. Search for the patient
3. Click the **Schedule action button**
4. Select the **scheduled request** to reschedule

**Same day — new time:**
- Left-click on the appointment
- **Drag** to the new desired time
- Click **Save**

**Different date and time:**
1. Right-click on the appointment
2. Click **Reschedule request**
3. Click **Release allocations** and accept the confirmation box
4. Change the date to the new desired date
5. Choose the new appointment time
6. Double-click the new time slot
7. Click **Save**

**After rescheduling:**
- Select the appropriate **service step change reason**
- Print appointment letter if needed (Save to print, Cancel to skip)
- Reception screen shows the new scheduled time
- You'll be notified there are reschedules on this request`
      },
      {
        title: 'Register a Scheduled Request (Step-by-Step)',
        content: `**When a scheduled patient arrives:**
1. Open **Reception** from the Main Menu
2. The screen displays patients **scheduled for today**, arrivals due soon, and overdue patients
3. Patient and request jackets displayed
4. Click the **Register action button**
5. All existing scheduled or received requests displayed — click the **desired request**
6. **Request dialog box** opens
7. Complete the **mandatory blue fields** and any other required information
8. Click **OK**

**Verification:**
- The request now shows as **"registered"** on the request jacket
- The request appears in the **waiting room worklist**`
      },
      {
        title: 'Register a Walk-In Request (Step-by-Step)',
        content: `**When an unscheduled patient arrives:**
1. Open **Reception** from the Main Menu
2. **Search** for the patient:
   - If already in Karisma — select the patient
   - If not — **fetch from host** and retrieve
3. Patient and request jackets displayed
4. Click the **Register action button**
5. If existing requests are shown, click **"New Request"**; if none, the dialog appears directly
6. Complete the **mandatory blue fields** and other required information
7. Click **OK**

**Result:** The newly created request appears as registered on the request jacket and in the **waiting room worklist**.`
      },
      {
        title: 'Receive a Request (Step-by-Step)',
        content: `**For receiving a request (e.g., faxed or electronic referral):**
1. Open **Reception** from the Main Menu
2. **Search** for the patient — select or fetch from host
3. Patient and request jackets displayed — **check patient info is correct**
4. Click **Receive**
5. The **receive request section** screen appears
6. Complete the **mandatory fields** and check all other information is correct
7. Click **OK**

**Result:**
- The reception screen shows the request with **"received"** next to the request identifier
- If applicable, click **Scan** to scan the referral form`
      }
    ]
  },
  {
    id: 'imaging',
    title: 'Imaging',
    subtitle: 'Service Management & Workflow',
    icon: 'Activity',
    color: '#DC2626',
    description: 'Manage imaging services, service types, departments, modalities, and the imaging workflow screen.',
    steps: [
      {
        title: 'Imaging Overview',
        content: `The Imaging screen manages the workflow for performing diagnostic imaging services.

**Key Tasks:**
- Check and update service details
- Complete verbal patient confirmations
- Print labels for imaging
- Scan worksheets
- Record service details (technologist, dose, etc.)
- Mark services as complete

**Accessing Imaging:**
Navigate to Menu > Imaging tile, or use the workflow view assigned to your profile.`
      },
      {
        title: 'Service Types',
        content: `Service Types represent the examinations offered (e.g., CT Brain, US Abdomen, MRI Knee).

**Elements that define a Service Type:**
- **Service Order Rule** — Rules for ordering the service
- **Service Perform Rule** — Rules for performing the service
- **Service Department** — Category (CT, US, MRI, NM, XR, etc.)
- **Report Type** — Associated report template
- **Service Step** — Workflow steps required
- **Service Procedure** — Specific procedure details
- **Service Modality** — Equipment modality
- **Service Type** — The complete definition combining all elements

**Practical Considerations:**
- Use a consistent coding convention for service types
- Name and Description fields should be clear and descriptive
- Service types link to scheduling for resource allocation`
      },
      {
        title: 'How a Booking Works (Backend)',
        content: `Understanding the backend flow helps troubleshoot booking issues.

**Flow:**
1. User selects a **time slot** in the diary
2. System checks **service procedure** requirements
3. System identifies required **resources** (room, equipment, staff)
4. System checks **resource availability** via operating profiles
5. System applies **resource conditions** (unavailable, designated, etc.)
6. If all resources are available, the **booking is confirmed**
7. The booking appears in the diary and relevant worklists

**Service Steps:**
Each service type has defined steps that must be completed (e.g., registration, imaging, reporting). Resources are attached to specific service steps with defined durations.`
      },
      {
        title: 'The Imaging Workflow Screen',
        content: `The Imaging screen presents a worklist of patients to be imaged, organised by workflow views.

**Worklist Features:**
- Patient tiles display patient name, service, and priority indicators
- **Primary highlight** (left margin) shows request context
- **Secondary highlight** (right margin) shows waiting time
- Tiles can be sorted by name, time, or priority
- Filter by specific services or departments

**Actions Available:**
- Open patient details
- View/edit service information
- Mark service steps as complete
- View scanned referral documents
- Add imaging notes`
      },
      {
        title: 'Commence a Service (Step-by-Step)',
        content: `1. Open **Imaging** from the Main Menu
2. Select your registered patient from the **"waiting room"** list
3. Patient and request jackets displayed — **check patient information is correct**
4. Click **Commence** — the commence dialog box displays patient details and service
5. Enter the **MRT details** and **room** (if applicable)
6. Answer any **confirmation questions** if asked
7. Click the **Commence** button

**Result:** The commenced service appears in the **"in procedure"** work list.`
      },
      {
        title: 'Verify a Service (Step-by-Step)',
        content: `1. Open **Imaging** from the Main Menu
2. Select your registered patient from the **"in procedures"** list
3. Patient and request jackets displayed
4. Click **Verify** — the verify dialog shows patient details and service
5. Complete the **relevant fields** and any **confirmation questions**
6. Click the **Verify** button

**Result:** The verified service appears in the **"recent requests"** work list.

**CRITICAL:** Do NOT verify early just to clear the list. Verification is the handoff point to both reporting and billing. Once verified, the study becomes eligible for the reporting queue and invoicing can proceed.`
      },
      {
        title: 'Add a Service (Step-by-Step)',
        content: `1. Open **Imaging** from the Main Menu
2. Select patient from **"in procedures"** or **"waiting room"** list
3. Click the **"+"** icon at the top right of the request details jacket
4. The **"Add Service Details"** dialog appears
5. Click the **ellipsis (...)** next to the **ordered service** field
6. In the **"Select Service Type"** dialog, search for the required service type
7. Press **OK** — the service populates the ordered service field
8. Press **OK** to return to the imaging workflow screen

**Result:** The additional service appears in a new service tile, awaiting commencement.`
      },
      {
        title: 'Edit a Service (Step-by-Step)',
        content: `1. Open **Imaging** from the Main Menu
2. Select patient from **"in procedures"** or **"waiting room"** list
3. Click the applicable **service tile** at the bottom of the request jacket
4. The **"Edit Service Details"** dialog appears
5. **Clear** the performed service field
6. Click the **ellipsis (...)** next to the performed service field
7. Search for the required **service type** and press **OK**
8. The new service populates the field — press **OK**

**Result:** The service tile reflects the edited service.`
      },
      {
        title: 'Cancel a Service (Step-by-Step)',
        content: `1. Open **Imaging** from the Main Menu
2. Select registered patient from the relevant work list
3. Check patient information is correct
4. On the relevant service tile, click the **blue cross [X]** to cancel
5. Choose the **appropriate cancellation reason**
6. Click **OK**

**Important Warnings:**
- If it is the **only service** on the request, the **whole request will be cancelled**
- If there is more than one service, the cancelled service appears in the **cancelled section** of the request jacket with the reason`
      },
      {
        title: 'Regress a Service Step (Step-by-Step)',
        content: `Use this when you need to undo a service step (e.g., accidentally commenced or need to re-do).

1. Open **Imaging** from the Main Menu
2. Select registered patient from the **"in procedures"** work list
3. Check patient information is correct
4. On the relevant service tile, click the **blue arrow** to "regress service step"
5. The regress service step **status options** appear — select the appropriate status to regress to
6. Click **Save** once

**Result:**
- Service tile shows the service step in **"not commenced"** status
- Patient tile moves into the **"waiting room"** work list
- The **"x"** next to performed start time indicates that value will be cleared`
      }
    ]
  },
  {
    id: 'reporting',
    title: 'Reporting',
    subtitle: 'Reports, Templates, Dictation & VR',
    icon: 'FileText',
    color: '#7C3AED',
    description: 'Create reports using templates, digital dictation, voice recognition, input fields, and manage report authorisation.',
    steps: [
      {
        title: 'Reporting Module Overview',
        content: `The Reporting module allows radiologists to create, edit, and authorise diagnostic reports.

**Key Features:**
- Digital audio dictation recording
- SpeechMagic voice recognition integration
- Report template system with input fields
- Report composition, editing, and formatting
- PACS integration for direct image access
- Multiple report statuses and workflows

**Accessing Reporting:**
Navigate to Menu > Reporting tile. The reporting workflow screen presents worklists organised into Report Decks and Sections.`
      },
      {
        title: 'The Reporting Screen',
        content: `The Reporting Screen is the central hub for all reporting activities.

**Key Areas:**
- **Report Deck** — Organises workflows into sections (e.g., Dictation, Review, Authorisation)
- **Section** — Presents a worklist based on configuration
- **Priority Count** — Shows count of Urgent, Due, or "Mine" reports
- **Priority Grouping** — Groups reports by Report Completion Priority
- **Grid Filter** — Quick search by Patient Name, Request ID, Service Name, or Requesting Practitioner
- **My Recent Reports** — Quick access to recently worked-on reports
- **Parked Reports** — Reports locked for later completion
- **Delayed Authorisation** — Reports waiting for deferred authorisation

**Layout Customisation:**
Configure and reorder columns in the grid. Options for Group By and Text Size adjustment are available.`
      },
      {
        title: 'The Report Dialog',
        content: `The Report Dialog is where you compose and format the actual report content.

**Toolbar Buttons:**
- Preview, Configure, Scheduling, Patient, Price, Notes, Request, Services, Practitioner, Manage Conditions, Priority, Attachments

**Information Displayed:**
- Patient details (name, identifiers, age, DOB)
- Request and service details (service code, date, request number)
- PACS integration link to images

**Report Actors:**
- **Typist** — Person transcribing the report
- **Primary Practitioner** — Main reporting radiologist
- **Secondary Practitioner** — Additional reporting radiologist
- **Supervisor Practitioner** — Supervising radiologist (for trainees)
- **Invoicing Practitioner** — Practitioner used for billing

**Word Processor Sections:**
- **Report Contents** — The main report body
- **Private Comments** — Internal notes (not printed)
- **Proposed Amendment** — Suggested changes
- **Released Contents** — Read-only final version`
      },
      {
        title: 'Report Templates',
        content: `Report Templates provide standard text and formatting for consistent reporting.

**Benefits:**
- Consistent wording of frequently used comments
- Consistent formatting within and across reports
- Improved efficiency for typists and radiologists
- Automatic formatting when using voice recognition

**Creating a Report Template:**
1. Navigate to **Data Management > Report Templates**
2. Click **New**
3. Assign a **Code**, **Name**, and **Category**
4. In the word processor field, enter the standard template content
5. Click **OK** to save

**Template Fields:**
- **Code** — Unique identifier (used for quick insertion)
- **Name** — Descriptive name
- **Description** — Detailed description
- **Category** — Grouping category
- **Department** — Associated department
- **Plain Text** — Text-only version
- **Outer** — Whether template wraps existing content
- **Voice Name** — Name used for voice recognition insertion
- **Available for all users** — Visibility setting
- **Restrict to Security users** — Limit access to specific users`
      },
      {
        title: 'Using Report Templates',
        content: `There are two methods to insert templates into patient reports:

**Method 1 — Using the Insert Button:**
1. Open the Report Dialog
2. Click the **"Insert Report Template"** button
3. Select the desired template from the list
4. Click **OK**

**Method 2 — Typing the Template Code:**
1. In the Report Contents section, type the first few letters of the template code
2. Press **Ctrl + Space**
3. A list of matching templates appears
4. Select the correct template

**Handling Input Fields in Templates:**
1. Move to the beginning of the report and press **TAB** to go to the first input field (shown in double brackets)
2. Enter the required information
3. Press **TAB** to move to the next field
4. For fields with predetermined responses, click the down arrow to see the dropdown list
5. Repeat until all fields are completed

**Note:** Do not remove the green shading, double brackets, or down arrow — these will not print on the report.`
      },
      {
        title: 'Inserting Input Fields into Templates',
        content: `Input fields make templates interactive, allowing users to fill in specific data.

**Input Field Types:**
| Type | Description | VR Compatible |
|------|-------------|---------------|
| **Boolean** | Yes/No radio buttons | No |
| **Number** | Number with set digits/decimals | No |
| **Integer** | Number with min/max range | No |
| **Date** | Date input (dd/mm/yyyy) | No |
| **Text** | Text input with character limit | Yes |
| **List** | Dropdown with predetermined values | No |
| **Paragraph** | Large text area | Yes |

**Steps to Insert an Input Field:**
1. Position your cursor in the template where you want the field
2. Right-click and select **"[] Input"** or use the toolbar input field icon
3. Configure the field:
   - **Name** — Descriptive identifier
   - **Hot Key** — Optional keyboard shortcut
   - **Width** — Field display width
   - **User can delete** — Allow users to remove the field
   - **Value required** — Make the field mandatory
   - **Correct input only** — Restrict to valid options
   - **VR Capitalisation Type** — For VR-compatible fields
   - **Type** — Select Boolean/Number/Integer/Date/Text/List/Paragraph
4. Click **OK**

**Inserting Standard Responses (List Fields):**
1. Follow steps above but select Type = **List**
2. Choose List Mode (Dropdown or Buttons)
3. Enter predefined values, one per line (e.g., Left, Right, Bilateral)
4. Check **"Correct input only"** to restrict input to these options`
      },
      {
        title: 'Digital Dictation',
        content: `Radiologists can dictate reports using digital audio recording.

**To Dictate a Report:**
1. Select the report from the worklist
2. Use the **Audio Control** to begin recording
3. Dictate the report content
4. Use audio controls:
   - **Record** — Start/resume recording
   - **Play** — Playback the recording
   - **Rewind** — Go back in the recording
   - **Fast Forward** — Skip ahead
5. Set the report status:
   - **In Progress** — Report is being worked on
   - **Complete** — Dictation is finished, ready for transcription

Audio files are stored as .wav format and linked to the report.`
      },
      {
        title: 'Voice Recognition Reporting',
        content: `Voice Recognition (VR) converts speech directly to text using SpeechMagic software.

**Prerequisites:**
1. Install a compatible microphone
2. Test hardware configuration
3. Install VR software (SpeechMagic)
4. Train the software to recognise your voice

**Dictating with VR:**
- Speech is entered directly as text in the report
- The process is similar to normal dictation but text appears immediately
- You can edit the recognised text as you go

**Using Templates with VR:**
- Templates with a Voice Name can be inserted by saying: **"template [voice name]"**
- Navigate input fields using voice commands:
  - **"Next field"** — Move to next input field
  - **"Previous field"** — Move to previous input field
  - **"Go to field [name]"** — Jump to a specific named field

**Note:** Only Text and Paragraph input field types are VR compatible.`
      },
      {
        title: 'Report Statuses & Authorisation',
        content: `Reports progress through defined statuses to ensure quality and accuracy.

**Process Statuses:**
- **Not Commenced** — Report has not been started
- **In Progress** — Report is being created
- **Parked** — Report is locked for later completion
- **Reported** — Dictation is complete
- **For Review** — Report is ready for review
- **For Second Review** — Requires additional review
- **Awaiting Delayed Authorisation** — In the delay period
- **Completed** — Report is finalised

**Clinical Availability Statuses:**
- **Not Released** — Report is not visible to clinicians
- **Preliminary** — Early/draft version visible
- **Authorised** — Final, authorised version
- **Amended** — Report has been changed after authorisation
- **Rework** — Report is being reworked
- **Withdrawn** — Report has been withdrawn

**Authorising a Report:**
Authorise reports when they are complete and final. Default distribution rules will automatically send the report to configured recipients.

**Delayed Authorisation:**
A safety net that defines a delay period before final authorisation. This gives radiologists time to review before the report is officially released.

**Parking a Report:**
Locks a report to allow later completion or prevent changes. Available for Not Commenced or In Progress reports.`
      },
      {
        title: 'Report Problem Types',
        content: `Reports can be flagged with known issues to track and resolve problems.

**Common Report Problem Types:**
- Missing request form
- Missing images
- Technical quality issues
- Incomplete clinical information
- Patient identification concerns

Flag reports using the Report Problem Types feature to ensure issues are tracked and resolved before authorisation.`
      }
    ]
  },
  {
    id: 'request-search',
    title: 'Request Search',
    subtitle: 'Find & Filter Requests',
    icon: 'Search',
    color: '#0891B2',
    description: 'Search for requests using identifiers, patient demographics, request notes, and custom filters.',
    steps: [
      {
        title: 'Request Search Overview',
        content: `The Request Search module enables you to find requests based on multiple criteria.

**Search Methods:**
- **Request Identifier** — Search by request ID or accession number
- **Patient Demographics** — Search by name, DOB, or other patient details
- **Request Notes** — Search within clinical, order, or scheduling notes
- **Custom Filters** — Create bespoke search criteria

**Accessing:**
Go to **Main Menu > Request Search**

**Note:** Access is controlled via Security Permission.`
      },
      {
        title: 'The Request Search Screen',
        content: `The screen is divided into several functional areas:

1. **Search Options** — Define which areas the text search will query (Request Identifier, Patient Demographics, or Request Notes)
2. **Search Button** — Execute the filter and display results
3. **Filter** — Select specific criteria using ADD, NOT, or OR logic
4. **Export** — Export grid results to XLSX or CSV file
5. **Grid** — Displays results in a table format:
   - Highlighted rows indicate the selected record
   - A **pill icon** indicates patient allergies
   - A **triangle icon** indicates patient conditions
   - Columns can be rearranged by dragging
6. **Preview** — Shows detailed information when a row is selected, including transport routing options if enabled
7. **View Action** — Open the selected request in:
   - **Reception** context
   - **Imaging** context
   - **Account Management** context`
      },
      {
        title: 'Creating Filters',
        content: `Create custom filters to quickly find specific types of requests.

**Steps to Create a Filter:**
1. Click the **Filter** icon in the Request Search screen
2. Click **Add** to create a new filter
3. In the "Add Filter" dialog:
   - **Name** — Enter a descriptive name (required)
   - **Search Text** — Optional text to include in the search
   - **Save filter for future use** — Check to save permanently
4. Add filter criteria from available categories:
   - **Status** — Request status (Registered, Scheduled, etc.)
   - **Patient** — Sex, Age Bracket, Allergy, Condition
   - **Practitioner** — Requesting practitioner, location
   - **Account Type** — Billing type
   - **Work Site** — Location
   - **Service Type** — Type of examination
   - **Dates** — Date ranges
5. Click **OK** to save

**Filter Management:**
- **Edit** — Modify an existing filter
- **Delete** — Remove a filter
- **Clone** — Duplicate a filter for minor adjustments
- **Apply** — Activate the selected filter

**Combining Filters:**
Filters can be combined using:
- **ADD** — Include additional criteria
- **NOT** — Exclude specific criteria
- **OR** — Include alternative criteria`
      },
      {
        title: 'Shared Filters & Search Views',
        content: `Key users can create and share filters with other users.

**My Filters vs Shared Filters:**
- **My Filters** — Created by you, visible only to you
- **Shared Filters** — Published to specific user groups by key users

**Creating Search Views:**
1. Navigate to **Data Management > Search Views**
2. Click **New**
3. Configure the Search View:
   - **Name** — Label for the view
   - **Type** — Screen type (Request or Report)
   - **Work Site** — Specific sites (empty = all sites)
   - **Text** — Default search text
   - **Discarded** — Include/exclude discarded items
   - **Filter** — Define the filter expression
4. Click **+ ADD FILTER** to define criteria
5. Click **Save**

**Publishing Search Views:**
1. Navigate to **Data Management > Workflow View Profiles**
2. Click **New** to create a profile
3. Name the profile (e.g., "Reception Searches")
4. Select the relevant search views
5. Click **Save**
6. Select the profile and click **Publish**
7. Choose destination users/roles/groups
8. Click **Publish**

**Important:** Users need to close and reopen Karisma for changes to take effect.`
      }
    ]
  },
  {
    id: 'report-search',
    title: 'Report Search',
    subtitle: 'Find & Filter Reports',
    icon: 'FileSearch',
    color: '#4F46E5',
    description: 'Search for reports using keywords, patient information, and custom filters with export capabilities.',
    steps: [
      {
        title: 'Report Search Overview',
        content: `The Report Search module allows you to create specific report queries using keywords and filters.

**Key Features:**
- Search report content by specific keywords
- Combine keywords with filters for precise queries
- Export results to Excel (XLSX or CSV)
- View and interact with found reports

**Accessing:**
Go to **Main Menu > Report Search**

**Note:** Access is controlled by Security Permission.`
      },
      {
        title: 'The Report Search Screen',
        content: `The interface includes these areas:

- **Search Bar** — Search by patient demographics or keywords
- **Search Button** — Execute the search
- **Filter** — Create specific criteria (worksite, service type, requesting practitioner)
- **Export** — Export to XLSX or CSV
- **Preview** — Report preview if available
- **Grid** — Displays report information with sortable columns
- **View Action** — Options to view, edit, dictate, distribute, or export as text

**Workflow:**
1. Enter search criteria in the Search bar or use filters
2. Click **Search**
3. Results display in the Grid
4. Use **View Action** to interact with individual reports`
      },
      {
        title: 'Filter Creation for Reports',
        content: `Create reusable filters for efficient report searching.

**Steps:**
1. Click the **Filter** icon
2. Click **New** in the filter interface
3. Configure the filter:
   - **Name** — Required identifier
   - **Search Text** — Specific words or phrases
   - **Save filter for future use** — Check to keep permanently

**Available Filter Criteria:**

**PATIENT:**
- Sex, Age Bracket, Allergy, Condition

**REQUEST:**
- Request Context, Work Site, Hospital, Ward
- Requesting Practitioner, Practitioner Location
- Unit, Condition Type, Referral Reason

**SERVICE:**
- Serviced Date, Service Step Status
- Service Type, Service Department, Service Modality
- Primary/Secondary Classification

**REPORT:**
- Discarded, Report Availability
- Has Report Problem, Report Problem Definition
- Actor, Actor Type
- Last Modified, Days Since Modified

**Drop-down Options:**
Each criterion offers three modes:
- **Show All** — Include everything
- **Exclude** — Remove specified items
- **Only Show** — Show only specified items`
      },
      {
        title: 'Search Views for Reports',
        content: `Search Views are shared filters that can be published to users.

**Creating a Report Search View:**
1. Navigate to **Data Management > Search View**
2. Click **New**
3. Configure:
   - **Name** — Descriptive label
   - **Type** — Select "Report"
   - **Work Site** — Specific sites or all
   - **Text** — Default search text
   - **Discarded** — Include/exclude
   - **Filter** — Click **+ ADD FILTER** to define criteria
4. Define custom filter criteria
5. Click **OK** then **Save**

**Publishing to Users:**
1. Go to **Data Management > Workflow View Profiles**
2. Create or edit a profile
3. Select the report search view
4. Save and **Publish** to users/roles/groups
5. Users must restart Karisma to see changes

**Security Permissions:**
- **Search report content** — Run existing shared filters
- **Manage Search Filters** — Create/manage own filters
- **Data Management** — Create search views and workflow profiles`
      }
    ]
  },
  {
    id: 'workflow-views',
    title: 'Workflow Views',
    subtitle: 'Customise Worklist Displays',
    icon: 'LayoutGrid',
    color: '#EA580C',
    description: 'Create and manage workflow views to customise how worklists appear for different users and departments.',
    steps: [
      {
        title: 'Workflow Views Overview',
        content: `Workflow Views customise the appearance and functionality of workflow screens by defining filters, sorting, and highlighting.

**Key Benefits:**
- **Consistency** — All users assigned to a view see the same information
- **Customisation** — Multiple views for different departments, roles, or sites
- **Efficiency** — Users see only relevant information

**Workflow View Types:**
Each view is associated with a specific workflow screen:
- **Approval** — Approval workflow
- **Imaging** — Imaging workflow
- **Order** — Order management
- **Reception** — Reception workflow

The available sorting, highlighting, and filtering options vary by type.`
      },
      {
        title: 'Creating a Workflow View',
        content: `**Steps to Add a New Workflow View:**
1. Navigate to **Data Management** from the Menu Screen
2. Search for **"Workflow Views"**
3. Click **New**
4. Configure the view:

**Basic Settings:**
- **Name** — Descriptive name (e.g., "CT Imaging")
- **Type** — Select the workflow type (Imaging, Approval, Order, Reception)
- **Work Site** — Assign to a specific location

**Sorting:**
- **Primary Sort** — First sort field (e.g., patient name)
- **Primary Sort Direction** — Ascending or Descending
- **Secondary Sort** — Second sort field (e.g., service time)
- **Secondary Sort Direction** — Ascending or Descending

**Highlighting:**
- **Primary Highlight** — Left margin colour (e.g., request context)
- **Secondary Highlight** — Right margin colour (e.g., waiting time)

5. Configure the **Custom Filter** (see next step)
6. Click **Save**`
      },
      {
        title: 'Custom Filters for Workflow Views',
        content: `Every Workflow View requires a custom filter to control which requests display.

**Imaging Workflow Filter Options:**
- **RELATED:** Current work site, Context, Hospital, Ward, Work site, Requesting unit, Responsible unit, Account type, Work area, Ward entity group
- **SERVICE:** Service, Service department, Service modality, Primary/Secondary classification, Allocated resource, Service type entity group
- **STATUS:** Registered, Scheduled, Received, Report/Service completion priority type, Request condition

**Approval Workflow Filter Options:**
- **REQUEST:** Context, Conditions, Hospital, Ward, Ward Entity Group, Work site
- **SERVICE:** Performed service type, Department, Modality, Primary/Secondary classification, Allocation
- **APPROVAL:** Assigned to, Held, Days since last modified
- **STATUS:** Received, Scheduled, Registered, Don't show scheduled/registered, Priority types
- **DUE:** Imaged By Due, Scheduled Due, Report Pickup Due

**Tip:** Start with broad filters and narrow down as needed. Overly restrictive filters may hide relevant requests.`
      },
      {
        title: 'Workflow View Profiles',
        content: `A Workflow View Profile is a collection of one or more Workflow Views, tailored for specific locations, departments, or roles.

**Creating a Profile:**
1. Navigate to **Data Management > Workflow View Profiles**
2. Click **New**
3. Configure:
   - **Name** — Descriptive name (e.g., "Worksite Name CT - Radiographer")
   - **Views** — Select the Workflow Views to include (e.g., "Imaging CT", "Approval CT")
   - **Discarded** — Option to include/exclude discarded items
4. Click **Save**

**Example:**
A CT Radiographer profile might include:
- "CT Imaging" view (showing only CT services in Imaging workflow)
- "CT Approval" view (showing CT services pending approval)`
      },
      {
        title: 'Publishing Workflow View Profiles',
        content: `Once created, profiles must be published to make them available to users.

**Steps to Publish:**
1. Navigate to **Data Management > Workflow View Profiles**
2. Select the profile to publish
3. Click **Publish**
4. In the Publish dialog:
   - **Constrain Users by Roles** — Filter by assigned roles
   - **Constrain Users by Groups** — Filter by Active Directory groups
   - **Search** — Find specific users
   - **Destination Profile Users** — Select individual users
5. Click **Publish**

**Important Notes:**
- Users must **restart their Karisma client** for changes to take effect
- If a profile is changed while a user is logged in, a warning will appear
- Multiple Workflow Views can be assigned to a single user profile for flexibility

**Alternative Method:**
Profiles can also be assigned directly via **Data Management > User Profiles** in the "Workflow view profile" field.`
      }
    ]
  },
  {
    id: 'accounting',
    title: 'Accounting',
    subtitle: 'Billing, Pricing & Invoicing',
    icon: 'DollarSign',
    color: '#16A34A',
    description: 'Configure charge management, pricing logic, invoice types, charge schemes, financial sites, and invoice reminders.',
    steps: [
      {
        title: 'Accounting Module Overview',
        content: `The Accounting module manages all financial aspects of the practice.

**Key Areas:**
- Account Types, Invoice Types, Invoice Rules
- Charge Items, Charge Levels, Charge Departments
- Charge Schemes and Charge Scheme Types
- Rebate Library and Charge Authority
- Financial Groups, Sites, and Periods
- Tax Rates
- Invoice Reminders and Document Designs
- Medicare Online Configuration

**Tables Used:**
Account Types, Invoice Types, Invoice Rules, Invoice Condition Types, Invoice Reminder Run Types, Invoice Reminder Stages, Invoice Span Design, Invoice Adjustment Reasons, Payment Adjustment Reasons, Charge Authority, Charge Schedules, Charge Scheme Types, Charge Items, Charge Levels, Charge Departments, Rebate Library, Financial Groups, Financial Sites, Financial Periods, Banks and Branches, BPay Configuration, Tax Rates.`
      },
      {
        title: 'Understanding the Pricing Flow',
        content: `Karisma uses a structured flow to determine pricing for services.

**The Pricing Logic (End-to-End):**

1. **End User** selects an **Account Type** (e.g., Medicare, Private, DVA)
2. Account Type links to an **Invoice Type**
3. Invoice Type links to **Invoice Rules** (per work/financial site)
4. Invoice Rules link to **Charge Level** and **Charge Scheme**
5. Charge Level/Scheme links to **Charge Items** and their pricing
6. **Charge Schedule** (Medicare update dates) affects which pricing applies
7. **Rebate Library / Charge Authority** (Medicare, DVA, AMA) provides base data

**Key Concept:**
The Account Type selected by the end user at registration ultimately determines which pricing rules are applied. Everything flows from that selection.`
      },
      {
        title: 'Rebate Library & Charge Authorities',
        content: `**Rebate Library:**
The core accounting engine for Medicare billing. Contains definitions for all billable items.

- Located in the menu screen
- Updated by Kestral support (live updates require consent and downtime)
- **For information only** — Key users should not directly modify

**Charge Authorities:**
Organisations responsible for payment.

**Common Authorities:**
- **Medicare Australia** — Government health insurance
- **DVA** (Department of Veterans' Affairs) — Veterans health cover
- **AMA** (Australian Medical Association) — Fee schedule

**Viewing Charge Authorities:**
1. Navigate to **Charge Management**
2. View **Charge Authorities** to see AMA, DVA, Medicare

**Charge Schedules:**
Map rebates to specific time periods for accurate billing based on request dates. New schedules appear with commence/end dates when Medicare issues updates (typically March and November each year).`
      },
      {
        title: 'Charge Items & Departments',
        content: `**Charge Items:**
Specific codes representing services (e.g., item 55812 for an ultrasound).

**Viewing Charge Items:**
1. Navigate to **Charge Management > Items**
2. Filter by schedule (e.g., most recent Medicare)
3. View item codes, descriptions, and pricing

**Manual Items:**
Create charge items not linked to any service for:
- Subpoenas
- USB/film charges
- Refunds
- Other consumables or non-rebatable items

**Charge Departments:**
Internal filters to categorise charge items.
- **US** — Ultrasound
- **CT** — Computed Tomography
- **MRI** — Magnetic Resonance Imaging
- **NM** — Nuclear Medicine
- **CR** — Default from rebate library

Departments can be created and assigned to items for internal filtering.

**Charge Levels:**
Define pricing rules for account types.
- **MBS** — Medicare Benefits Scheme schedule fee
- **Private** — For non-rebatable services`
      },
      {
        title: 'Creating Charge Schemes',
        content: `Charge Schemes define custom pricing, commonly used for private billing gap amounts.

**Steps to Create a Charge Scheme:**
1. Navigate to **Charge Management > Charge Schemes**
2. Click **New**
3. Configure:
   - **Code** — Unique identifier (e.g., "NT200")
   - **Name** — Descriptive name (e.g., "Nuclear Translucency at $200")
   - **Scheme Type** — Link to account types (Private, Overseas, Pensioner)
   - **Authority** — Select paying authority (e.g., Medicare Australia)
   - **Schedule** — Select relevant schedule date
   - **Tariff Type** — "Fixed Amount" or "Percentage"
   - **Fixed Amount / Amount Gap** — Enter the dollar value
   - **Percentage** — Enter the percentage value
   - **Rounding Options** — For percentages (e.g., round up to five cents)
   - **Maximum Tariff** — Cap on total gap payment per request
4. Click **Save**

**Linking Charge Schemes to Items:**
1. Go to **Charge Management > Charge Schemes**
2. Select the created scheme
3. Under **Items**, click **Add Item**
4. Search for the charge item code (e.g., 55707 for NT scan)
5. Save

**Important:** Ensure the item is correctly linked to the service type in **Data Management > Service Types** to avoid errors.`
      },
      {
        title: 'Testing Pricing',
        content: `Always test pricing configuration before going live.

**Steps to Test:**
1. Go to a patient record
2. Select the appropriate **Account Type**
3. Click **"Price"** (or **"Price Request"** from the request screen)
4. Review the displayed pricing:
   - **Rebate amount** — What Medicare/authority pays
   - **Gap payment** — What the patient pays
   - **Total fee** — Combined amount

**What to Verify:**
- Correct charge items are being applied
- Rebate amounts match current Medicare schedule
- Gap amounts match your charge scheme configuration
- Maximum tariff caps are working correctly

**Tip:** Test with different account types (Medicare, Private, DVA) to ensure all pricing paths are correct.`
      },
      {
        title: 'Invoice Rules & Types',
        content: `**Invoice Rules:**
Define invoice characteristics including charged amounts and tax rates.

**Options include:**
- Zero charge
- Rebate charge
- Rebate declared fee
- Request separation rules

Invoice Rules link charge levels and schemes to invoicing logic.

**Invoice Types:**
Allow site-specific flexibility when recording account types against requests. Each Invoice Type links to Account Type and can contain multiple Invoice Rules.

**Account Types:**
The primary selection for end-users during registration. Common types:
- Medicare
- Private
- DVA (Veterans)
- Overseas
- Pensioner
- Workers Compensation`
      },
      {
        title: 'Financial Groups, Sites & Periods',
        content: `**Financial Groups:**
Top-level organisational units for financial data (e.g., "SP-Main", "SP-Ultrasound").

1. Navigate to **Data Management > Financial Groups**
2. View or create groups for logical financial separation

**Financial Sites:**
Specific locations requiring separate financial tracking.

1. Navigate to **Data Management > Financial Sites**
2. Create sites linked to financial groups
3. Configure:
   - Address
   - "Suppress Accounting" — Disable accounting for this site
   - **"Lock Service After Invoicing"** — Prevents modification after invoicing (recommended: enabled)

**Financial Periods:**
Monthly periods for accounting purposes.

1. Navigate to **Data Management > Financial Periods**
2. Generate periods (typically monthly)
3. These enable invoicing and financial reporting

**Tax Rates:**
1. Navigate to **Data Management > Tax Rates**
2. Define rates (e.g., 10% GST)
3. Note: Most medical imaging services are tax-exempt in Australia
4. Tax may apply to consumables (USB films, non-medical items)`
      },
      {
        title: 'Invoice Reminders & Document Design',
        content: `**Setting Up Invoice Reminders:**
1. Navigate to **Data Management > Invoice Reminder**
2. Define **Stages** with escalating messages:
   - Stage 1: 7 days — Friendly reminder
   - Stage 2: 14 days — Second notice
   - Stage 3: 30 days — Final notice
3. Define **Run Types** (e.g., "Private Invoice")
4. Set **Minimum Outstanding Amount** (e.g., $20 — no reminder below this)
5. Link Run Types to Stages

**Designing Financial Documents:**
1. Navigate to **Data Management > Document Designs**
2. Select document type:
   - **Pricing/Quote** — Includes disclaimer and validity period
   - **Tax Invoice** — Includes remittance advice with bank details
   - **Invoice Reminder** — Includes escalating reminder messages
   - **Receipt** — Summarises payment received
3. Customise templates including text, payment options, and remittance advice

**Invoice Span Designs:**
Configure table layouts within invoices.
1. Navigate to **Data Management > Span Designs**
2. Select the span design (e.g., "Price", "Private")
3. Add, delete, or reorder columns (item number, service name, amount, gap, rebate)
4. Adjust column widths and data styles`
      },
      {
        title: 'Medicare Bulk Bill Account Type',
        content: `Setting up Medicare Bulk Billing — where the provider bills Medicare directly and accepts the rebate as full payment (no out-of-pocket for the patient).

**Steps to Define:**
1. **Define Medicare card as patient identifier** — Data Management > Patient Identifier Types > New, Style = "Australian Medicare Number"
2. **Create Patient Identifier Type Rule** — Data Management > Patient Identifier Type Rules > New, set "Visible on patient dialog" = yes
3. **Configure Invoice Rule** — Accounting > Tasks > Invoice Rules > New:
   - **Charge Authority** = Medicare Australia
   - **Charge Level** = MBS (Medicare Benefits Schedule)
   - **Rebate charge amount override** = MUST be selected (85% outpatient / 75% inpatient)
   - **Allow inpatient/outpatient/emergency context** = select as needed
   - **Consolidate by patient + request context + invoicing practitioner** = ALL THREE MUST be selected (Medicare requirement)
   - **Account requires multiple procedure override text** = select
   - **Medicare Online Form Type** = Bulk Bill Assignment Advice (for DB4 form)
   - Only ONE bulk bill invoice rule needed for entire practice
4. **Create Invoice Type** — Accounting > Tasks > Invoice Types > New, then Configure Invoice Rules per Financial/Work Site
5. **Define Account Type** — Accounting > Tasks > Account Types > New:
   - Patient identifier type requirement = Medicare Identifier
   - Invoice Type = bulk bill invoice type
   - Default debtor = Charge Authority Medicare Australia
   - Enforce default debtor = MUST be selected
   - Request patient context = Bulk Bill
6. **Create Invoice Batch Type** — Accounting > Tasks > Invoice Batch Types > New:
   - Usage type = Bulk Bill
   - Maximum invoice count = 65 (Medicare limit is 75 items per batch)
   - Separate Inpatient and Outpatient batches (emergency goes with outpatient)
7. **Configure DB4 printer rules** — Data Management > Distribution Rules > New

**Medicare Online Workflow:**
- Invoices generated on-the-spot at reception or batch-generated
- Patient receives copy of DB4 form
- Invoices batched by inpatient/outpatient and provider
- Submitted electronically to Medicare Australia
- Medicare returns exception and EFT report for processing`
      },
      {
        title: 'Private Patient Account Type',
        content: `Private billing — patient billed directly, claims Medicare rebate themselves, pays the "gap" (out-of-pocket expense).

**Three Methods for Setting Private Fees:**

**Method 1 — Custom Charge Level (Dollar Amounts):**
1. Create charge level: **Charge Management > Levels > New** (Code e.g., "Priv")
2. Set individual amounts per item: **Charge Management > Items** > filter schedule > select item > Levels > add amount
- Note: Amounts do NOT auto-update with rebate refreshes — manual review required

**Method 2 — Fixed Dollar Gap:**
1. Create Charge Scheme Type: **Charge Management > Scheme Types > New** (e.g., "80Gap")
2. Create Charge Scheme: **Charge Management > Schemes > New**:
   - Tariff type = FixedAmount
   - Fixed Charge Amount = dollar value
   - Maximum tariff = cap per patient per period
3. Assign items: Select scheme > Add Item > select charge items
- Advantage: Consistent gap amount even after rebate updates

**Method 3 — Percentage Gap:**
1. Create Charge Scheme Type (e.g., "10P")
2. Create Charge Scheme: Tariff type = Percentage, set %, Rounding Type/Denomination
3. Assign items same as Method 2
- Advantage: Consistent percentage even after rebate updates

**Invoice Rule Settings:**
- Charge Scheme Application Types: Highest Tariff / Lowest Tariff / One Per Scheme / All Tariffs
- Rebate Charge Amount Override = enable for Methods 2 & 3
- Maximum Tariff per Patient = cap per day

**Invoice must include:** Practitioner name + Provider Number, Patient name, Item number + description, Service date, Amount charged, Payment/remittance section`
      },
      {
        title: 'Workers Compensation Account Type',
        content: `Workers Compensation billing — each state has its own rules for charging employers/insurance companies.

**Steps to Define:**
1. **Create charge level** — Charge Management > Levels > New (one per WC organisation)
2. **Upload state-specific fee file** into the charge level (download from state websites)
3. **Configure Invoice Rule:**
   - Charge Authority = **Medicare Australia** (all states except NSW) or **AMA** (NSW only)
   - Charge Level = the WC charge level created
   - NSW: Rebate charge amount override = must be selected
   - Consolidate by patient + request context + invoicing practitioner = ALL THREE must be selected
4. **Create Invoice Type** and configure per Financial/Work Site
5. **Define WC Account Type** — Accounting > Tasks > Account Types > New
6. **Ensure invoice document design** contains required WC fields:
   - Invoice.Recipient.Name.FullName
   - Invoice.Recipient.Contact.Address.Location
   - Invoice.Patient.Human.Name
   - Invoice.Request.Claim.Number.List
7. **Define employers/insurance companies** as Institutions — Accounting > Tasks > Institutions > New

**State-Specific Rules:**
- All states except NSW: own fees for Medicare item numbers, same rules as Medicare
- **NSW**: must use AMA item numbers and fees, employers pay full AMA fee (except 2 MRI items)

**Fee File Sources:**
- Victoria: worksafe.vic.gov.au
- NSW: safework.nsw.gov.au / icare.nsw.gov.au / sira.nsw.gov.au
- Queensland: workcoverqld.com.au
- SA: safework.sa.gov.au
- WA: workcover.wa.gov.au
- Tasmania: worksafe.tas.gov.au
- NT: worksafe.nt.gov.au

WC invoices can be generated on-the-spot by reception or in weekly batches as a back-office task.`
      }
    ]
  },
  {
    id: 'user-profiles',
    title: 'User Profiles',
    subtitle: 'Profile Configuration & Settings',
    icon: 'UserCog',
    color: '#9333EA',
    description: 'Configure user profiles including appearance, behaviour, reporting settings, and workflow assignments.',
    steps: [
      {
        title: 'User Profiles Overview',
        content: `User Profiles control how Karisma appears and behaves for each user. They are configured in **Data Management > Profiles**.

**Four Configuration Sections:**
1. **Details** — Basic profile information
2. **Appearance** — Visual presentation settings
3. **Behaviour** — Interaction and workflow settings
4. **Reporting** — Reporting module configuration

**Important:**
- Profiles can be created before a user logs in (if synced via Security Directory)
- This is especially beneficial for Reporting Users (Doctors, Typists) to ensure they can start immediately
- Generic users need minimal configuration; Reporting users need detailed setup`
      },
      {
        title: 'Details Section',
        content: `The Details section contains basic identification and access information.

**Fields:**
- **Code** — Unique identifier for the profile
- **Name** — Display name (e.g., "Doctor", "Reception")
- **Description** — Brief description of the profile's purpose
- **Security User** — Links the Active Directory account to the profile
  - Automatically added via AD synchronisation
  - Manually link if creating profile directly
- **Expiry Date** — Date after which the profile is no longer valid
- **Discarded** — Checkbox to mark profile as inactive
- **Is Licensed** — Whether the user has a dedicated user token

**Creating a New Profile:**
1. From Menu, click **Data Management**
2. Search for **"User Profiles"**
3. Click **New**
4. Fill in the required fields
5. Click **Save**`
      },
      {
        title: 'Appearance Section',
        content: `Controls how information is presented to the user.

**Settings:**
- **Colour scheme** — Sets the overall colour theme
- **Workflow view profile** — Assigns specific workflow views (multiple views possible)
- **Workflow practice management configuration** — Assigns practice management config (one only)
- **Print preview fit mode** — How print previews fit the page
- **Print preview scale** — Scale percentage for print previews
- **Request form preview appearance** — How request forms display in preview
- **Request form properties appearance** — How request form properties display
- **Display service time on history** — Show/hide service times in patient history`
      },
      {
        title: 'Behaviour Section',
        content: `Determines how Karisma interacts with the user, especially for reporting.

**Settings:**
- **Comparison application** — External app for comparison (e.g., WinMerge)
- **Auto logout idle timespan** — Time before automatic logout due to inactivity
- **Allow auto completion** — Enable auto-complete for lists
- **Transcription auto rewind duration** — How far audio rewinds automatically
- **Default delayed authorisation period** — Default delay before report authorisation
- **For Review Warning Count** — Number of outstanding reports before a warning appears
- **Push to talk dictation** — Enable "dead man switch" dictation mode
- **Scale request form on scroll** — Mouse scroll controls zoom vs. navigation
- **Show report pricing detail** — Display pricing in Report Dialog
- **Release preliminary report by default** — Auto-select "Release Preliminary"
- **Show request recipients on report dialog** — Display recipients in Edit Report Details
- **Confirm when withdrawing a report** — Require confirmation for report withdrawal
- **Foxo URL** — URL for Foxo integration`
      },
      {
        title: 'Reporting Section',
        content: `Defines how the Reporting Module works for this user.

**Settings:**
- **Enable VR for reporting** — Turn on Voice Recognition
- **VR is reporting default** — Make VR the default method
- **Enable reporting auto batch mode** — Open reports sequentially, locking current and next
- **Performing provider resource** — Show diary allocations on Reporting screen
  - Must be linked to a worksite resource with "Doctor" style
- **Live updates** — Enable real-time data updates
- **Data refresh period** — Interval for data refreshes
- **Diary refresh period** — Interval for diary refreshes
- **Verified to overdue period** — Time before flagging as overdue (image verification to pre-dictation)
- **Reported to overdue period** — Time before flagging as overdue (dictation to typing)
- **Due period prior to report pick up time** — Overdue flag based on report pickup time
- **Amount of days considered recent** — What counts as a "recent" report
- **Report Decks** — Add applicable Report Decks to appear in the Reporting Workflow screen

**Key Distinction:**
- **Generic Users** — Minimal configuration (details + appearance)
- **Reporting Users** — Full configuration required (all four sections)`
      }
    ]
  },
  {
    id: 'service-conflicts',
    title: 'Service Conflicts',
    subtitle: 'Conflict Rules & Alerts',
    icon: 'AlertTriangle',
    color: '#D97706',
    description: 'Configure service conflict rules to alert staff about potentially conflicting service combinations.',
    steps: [
      {
        title: 'Service Conflicts Overview',
        content: `Service Conflicts alert operators about other services that may conflict when booking a new service.

**Purpose:**
When booking a service, operators can be warned if relevant services were recently performed, or are scheduled to be performed soon.

**Examples:**
- A recent MRI brain may be relevant when ordering a CT brain
- A patient being "hot" from a nuclear medicine service may affect ultrasound staff
- A recent CT with contrast may conflict with another contrast study

**Configuration Components:**
All configured in **Data Management**:
1. **Service Type Groups** — Define groups of related services
2. **Service Conflict Reasons** — Descriptive reasons for conflicts
3. **Service Conflict Rules** — The actual conflict detection logic`
      },
      {
        title: 'Service Type Groups',
        content: `A Service Type Group defines a collection of Service Types, either directly or via categories.

**Fields:**
- **Name** — Descriptive name for the group
- **Service Type** — Specific service type members
- **Service Department** — Department members (e.g., CT, US, MRI)
- **Anatomical Body Site** — Body site members (e.g., Brain, Abdomen)
- **Service Modality** — Modality members
- **Service Technique** — Technique members
- **Service Primary Classification** — Primary classification members
- **Service Secondary Classification** — Secondary classification members
- **Disabled** — Checkbox to disable the group

**Example:**
Create a group called "Nuclear Medicine Services" and set Service Department = Nuclear Medicine. This captures all NM services automatically.`
      },
      {
        title: 'Service Conflict Rules',
        content: `Rules are central to the conflict system. When an operator saves a request, rules are checked and warnings are presented.

**Rule Fields:**
- **Input Group** — The service type group being booked
- **Match Group** — The group to check for conflicts (can be empty)
- **Match Both Directions** — Check conflicts in both directions
- **Match Same Department** — Match by same department
- **Match Same Body Site** — Match by same anatomical body site
- **Match Same Modality** — Match by same equipment modality
- **Match Same Technique** — Match by same technique
- **Match Same Primary/Secondary Classification** — Match by classification
- **Reason** — Why this conflict matters
- **Before Time** — How far into the past to check
- **After Time** — How far into the future to check
- **Show Status on Patient Jacket** — Display conflict status on patient record
- **Disabled** — Turn off the rule

**Time Matching Logic:**
- The system uses **performed time** if the service has been commenced
- Otherwise, it uses **scheduled time**
- Walk-in services that haven't commenced are ignored`
      },
      {
        title: 'Example: Same Department Warning',
        content: `**Scenario:** Warn users about existing services from the same department within the last two weeks.

**Steps:**
1. Create a **Service Type Group** representing the services to match
   - Set the **Service Department** field to the relevant department
2. Create a **Service Conflict Rule**
   - Set the created group as the **Input Group**
   - Check **"Match same department"**
   - Set **Before Time** to 14 days
   - Configure an appropriate **Reason** (e.g., "Patient had a recent same-department exam")
3. Click **Save**

**Result:** When someone schedules a service for the configured department and another was already performed within 14 days, a warning appears during request validation.

**Tip:** To also check future bookings, set the **After Time** field to 14 days as well.`
      },
      {
        title: 'Example: Radioactive Patient',
        content: `**Scenario:** Alert ultrasound staff when a patient recently had a nuclear medicine service.

**Steps:**
1. Create a **Service Type Group** for Nuclear Medicine department
2. Create another **Service Type Group** for Ultrasound department
3. Create a **Service Conflict Rule**:
   - Set **Input Group** = Ultrasound group
   - Set **Match Group** = Nuclear Medicine group
   - **Do NOT** check "Match Both Directions"
   - Check **"Show Status on Patient Jacket"** to display patient status
   - Set **Before Time** to the "hot" period (in hours)
   - Configure **Reason** (e.g., "Patient may be radioactive from recent NM service")
4. Click **Save**

**Important:** Do NOT set "Match Both Directions" here. You want ultrasound staff warned about NM patients, but not NM staff warned about ultrasound patients.

**Result:** When scheduling an ultrasound within the configured time after a nuclear medicine service, a warning appears. The patient jacket also shows the radioactive status.`
      },
      {
        title: 'Example: Same Body Site Warning',
        content: `**Scenario:** Check for recent services on the same body site (e.g., recent brain MR when scheduling brain CT).

**Steps:**
1. Create a **Service Type Group** with the relevant **Anatomical Body Site**
2. Create a **Service Conflict Rule**:
   - Use the group as the **Input Group**
   - Check **"Match same body site"**
   - Set **Before Time** to 14 days
   - Configure **Reason**
3. Click **Save**

**Result:** Warning appears if someone schedules a service for a body site that already has a recent service within 14 days.

**Note:** This only works correctly for services that have an anatomical body site configured in their service type definition. Ensure body sites are properly set up in Data Management.`
      }
    ]
  },
  {
    id: 'system-admin',
    title: 'System Admin',
    subtitle: 'Security, Documents & Distribution',
    icon: 'Shield',
    color: '#475569',
    description: 'Manage security settings, user access, document design, print distribution, and system configuration.',
    steps: [
      {
        title: 'Security Directory Overview',
        content: `The Security Directory controls access to Karisma via unique logins and customised permissions.

**Key Concepts:**
- Karisma uses **Windows Active Directory** for user/group management
- Security permissions are assigned via groups
- Each user requires a unique login/password

**Accessing Security:**
Navigate to Menu > Security Directory

**Security Directory Options:**
- **Administration Tasks:**
  - Resync Users & Groups from Active Directory
  - View Connected Sessions
- **Security Tables:**
  - Authorities
  - Users
  - Groups
  - Roles`
      },
      {
        title: 'Security Users, Groups & Roles',
        content: `**Security Authorities:**
Identify your site's domain and interaction level with Karisma.

**Security Users:**
Individuals or systems permitted to access Karisma. Each requires:
- A user account in Active Directory
- A corresponding Karisma user record
- Appropriate group memberships

**Security Groups:**
Group users with common functions for easier permission management.
- Example: "Radiographers" group, "Reception" group, "Doctors" group
- Permissions assigned to groups apply to all members

**Security Roles and Permissions:**
Define roles with specific permissions to control Karisma functions.

**Common Permissions:**
- Search report content
- Manage Search Filters
- Data Management access
- Reporting access
- Accounting access
- Security administration`
      },
      {
        title: 'Document Design',
        content: `Karisma allows design and maintenance of various document types.

**Supported Document Types:**
- Invoice
- Patient Label
- Request Label
- Practitioner Label
- Appointment Letter
- Report
- Account Statement
- Patient Recall Letter
- FTA Appointment Letter

**The Design Tab has Four Elements:**

1. **Toolbar** — Quick access buttons: Clear, Open, Save, Snap, Zoom, Print, Refresh
2. **Design Panel** — Tree structure showing text, database fields, and graphics
3. **Palette Panel** — Components for design:
   - **Arrow** — Directional indicators
   - **Field** — Database field placeholders
   - **Container** — Grouping elements
   - **Text** — Static text blocks
   - **Rich** — Rich formatted text
   - **Image** — Graphics and logos
4. **Document Layout Panel** — Visual preview of the design

Users have full control over content, layout, and document size.`
      },
      {
        title: 'Distribution & Printing',
        content: `The Distribution module manages how documents are sent to recipients.

**Printers:**
- Supports local, shared, and network printers
- Define managed printers and trays in Karisma

**Non-Report Printing:**
Covers labels, invoices, receipts, letters, etc.
- Processed by user-defined **distribution rules**
- Rules define: what to print, how to initiate, where to print
- Rules are assessed by sequence number (first matching rule executes)

**Automated Report Distribution Modes:**
- **Managed Printing** — Direct print to configured printers
- **Printing Batch** — Batch print multiple reports
- **Faxing** — Send reports via fax
- **Email** — Email reports to recipients
- **Partner Product** — Send to integrated systems
- **SMS** — Send notification via text message

**Distribution Control Elements:**
- Report constraints — When distribution applies
- Report recipient rules — Who receives the report
- Contact groups — Groups of recipients
- Entity groups — Organisational groupings
- Delivery methods — How reports are delivered
- Report destination rules — Where reports go`
      },
      {
        title: 'Implementation & Testing',
        content: `Karisma implementation follows a structured approach.

**Phase 1: Implementation Planning**
- Site analysis (workflow analysis + gap analysis)
- Identify functionality gaps

**Phase 2: RIS Implementation**
- Environment deployment
- Module configuration
- Interface testing (SIT)
- Data migration and verification
- User Acceptance Testing (UAT)
- End-user training
- Go-live preparation

**Phase 3: Go Live**
- Managed go-live with Kestral support on-site
- Outstanding issues resolved/logged
- Post-implementation review
- Official handover to support

**Testing Types:**
- **UAT** — User Acceptance Testing (verify requirements are met)
- **Scenario Testing** — Test specific workflow scenarios
- **Stress Testing** — Test system under load
- **Parallel Testing** — Run old and new systems simultaneously

**Important:** Client testing sign-off is required before going live.`
      },
      {
        title: 'Contacting Kestral Support',
        content: `Kestral provides ongoing support post-installation.

**When Reporting Issues, Provide:**

**For Enhancements:**
- Detailed specification
- Applicability to the product
- Examples
- Time constraints
- Willingness to pay for custom development

**For Errors/Failures:**
- When the problem occurred
- Recent system changes
- Error messages
- Exact steps to reproduce
- Patient details (internal number/UR number)
- Request/service details
- Hard copy information

**Ticket Priority Levels:**
| Priority | Description |
|----------|-------------|
| **P1 Critical** | Server crashes, system inaccessible, major module unavailable |
| **P2 Urgent** | Severely affects system, no workaround |
| **P3 Important** | Affects workflow, workaround available |
| **P4 Normal** | Needs attention, acceptable workaround exists |
| **P5 Low** | Not expected in short/medium term |

**Escalation:** Log ticket > Written communication > Support Manager > CEO`
      },
      {
        title: 'Security Permissions Reference',
        content: `Karisma uses role-based permissions. Permissions are assigned to **Security Roles** > **Security Groups** > **Security Users**.

**"Default" means the permission is NOT granted.** Most permissions must be explicitly granted via a Role.

**Workflow Permissions (screen access):**
- **Accounting** — Access Accounting workflow
- **Receptionist** — Access Reception workflow
- **Imaging** — Access Imaging workflow
- **Reporting** — Access Reporting workflow
- **Diary** — Access Scheduling Diary
- **DataManagement** — Access Data Management
- **PracticeManagement** — Access Practice Management
- **RequestSearch / ReportSearch** — Access search screens
- **PractitionerManagement** — Access Practitioner Management
- **ResourceManagement / ServiceManagement** — Access resource/service screens

**Report Permissions:**
- **Edit** — Commence and modify reports
- **Authorise** — Release reports, set to complete
- **Amend** — Change reports after authorisation
- **Withdraw** — Withdraw authorised reports
- **Rework** — Further work post-authorisation
- **Dictate** — Dictate reports and enter findings
- **Preliminary** — Release preliminary reports
- **SendForReview / SendForSecondReview** — Send for review

**Request Permissions:**
- **ChangeServiceStepStatus** — Use Commence, Claim, Complete, Verify buttons
- **EditServiceRelatedInformation** — Edit service info from jackets
- **ReinstateService** — Reinstate cancelled services
- **RegressServiceStepFromVerified** — Regress from verified
- **Obsolete** — Discard/recover requests

**Patient Permissions:**
- **Search** — Access patient search
- **Merge** — Merge duplicate patients
- **ManageVIP** — Manage VIP flag
- **Unprotect** — Edit protected details

**Invoice & Payment Permissions:**
- **Invoice.Modify** — Edit invoices and items
- **Invoice.Extract** — Use extract actions
- **Payment.Process** — Process payments/refunds
- **Payment.ManageBatch** — Manage banking lists

**Key Admin Permissions:**
- **Main.MenuAccess** — Access main menu (fundamental)
- **Main.Find** — Full-text search
- **Charge.Manage** — Manage charge authorities, items, levels
- **Security.ManageDirectory** — Manage users, groups, roles
- **PracticeManagement.BreakLocks** — Break locks on reports/requests/patients

**Kestral-Only (should be denied):** Developer, Rebate.Configure, Deployment.AgentService, Instrument.AccessPanel`
      },
      {
        title: 'SP Workflow Documentation',
        content: `Site-specific workflow for Sanctuary Point Radiology and Sanctuary Point Ultrasound.

**Patient Journey:** Booking > Reception > Imaging > Reception > Reporting > Billing

**1. Booking — Manage the Referral:**
- Patient receives **SMS reminder 1 day before** appointment
- Optional appointment letters distributed

**2. Reception — Patient Arrives:**
- Multiple sites have **separate reception workflows**
- SP may require additional request forms at registration
- Verify patient details, register request, scan referral

**3. Imaging — Image the Patient:**
- Multiple sites have separate imaging workflows
- Claim, commence, complete, record, verify services

**4. Reception — Patient Leaves:**
- Invoice adjustment: **Patient Balance > select unpaid invoice > click item to adjust**
- Process upfront payments for private/pensioner patients

**5. Reporting — Report on Images:**
- External Voice Recognition (VR) used
- Reports **automatically distributed** to referring practitioner and eligible recipients

**6. Billing — Account Type Payment Flows:**

| Account Type | Code | Billing Flow |
|---|---|---|
| Medicare Bulk Bill | M | After Imaging > Invoice Batch > Claim via Medicare Online |
| Veterans Affairs | V | After Imaging > Invoice Batch > Claim via Medicare Online |
| Private Medicare | P | After Imaging > Upfront payment + Medicare Claim |
| Pensioner | PEN | After Imaging > Upfront payment + Medicare Claim |
| Overseas | O | After Imaging > Upfront payment (full amount) |
| Workcover | W | After Reporting > Issue All Invoices via Debtor Page |

**Manual Payments:** Received via Debtors Page
**Medicare Online:** Payments distributed at 12:00 AM — process batches in the morning`
      }
    ]
  },
  {
    id: 'practice-management',
    title: 'Practice Management',
    subtitle: 'Site Workflow & Investigation Hub',
    icon: 'Monitor',
    color: '#0F766E',
    description: 'Central hub for managing site workflows, investigating records, handling distribution issues, managing duplicate patients, and monitoring system health.',
    steps: [
      {
        title: 'Practice Management Overview',
        content: `The Practice Management screen is a central hub for managing site workflows, investigating records, and resolving issues.

**Access:** Main Menu > Practice Management (requires Security Permission)

**Work Site Filter:** Click the Worksite button (top right) to filter by specific Work Site(s) or view all.

**5 Main Areas (left to right):**
1. **Sections** — Categories of information (Dashboard, Distribution, Orders, Reports, Requests, SMS, Wait Lists)
2. **Dashboard** — Customisable checks for regular viewing
3. **Patients** — Patient-specific management
4. **Actions** — Direct task execution
5. **Browse** — Search and view detailed records`
      },
      {
        title: 'Sections: Distribution & Orders',
        content: `**Distribution Section:**
- **Failed Jobs** — Active count of failed distribution jobs. Options to Resend or mark as Resolved. Common causes: invalid fax/SMS numbers, delivery issues, HL7 message problems
- **Claimed Jobs** — Jobs with status "Claimed by Agent"
- **Waiting Jobs** — Jobs with status "Waiting for Delivery"
- **Recent Jobs** — Distribution jobs from the past hour

**Orders Section:**
- **Failed Orders** — Active count of failed orders
- **Rejected Messages** — Active count of rejected orders with options to resolve, recover, or process them`
      },
      {
        title: 'Sections: Reports',
        content: `**Reports Section:**
- **Failed Delayed Authorisation** — Delayed authorisations that haven't been distributed. Options to manage and complete distribution
- **Locked Reports** — Reports locked by users (requires permissions to unlock)
- **Problem Reports** — Reports with a Report Problem Type flag set. Action and remove flags
- **Recently Modified Reports** — Reports modified in the past 7 days
- **Incomplete Reports** — Reports in "Not Released" or "Preliminary" clinical availability status
- **Failed Incoming Result Messages** — Failed due to missing config or locked records. Options: ignore, recover, or process`
      },
      {
        title: 'Sections: Requests',
        content: `**Requests Section:**
- **Imaging Due Received Requests** — "Received" status requests with an Imaging Due Date
- **Received Requests** — "Received" status, not yet Scheduled or Registered
- **Unverified Requests** — Registered with unverified services
- **Invoicing Suppressed Requests** — Requests with "Suppress Invoicing" set (prevents invoicing)
- **Unbooked Scheduled Requests** — Scheduled but no diary time allocation. Options: Schedule or Cancel
- **Unregistered Scheduled Requests** — Scheduled but not yet registered
- **Today's Cancelled Service Steps** — Service steps cancelled today (can be reinstated)
- **Missing Admission Requests** — Requests without a visit number (requires PMI feed)
- **Admission Time Mismatch** — Service date falls outside admit/discharge date
- **Locked Requests** — Locked by users (requires permissions to unlock)`
      },
      {
        title: 'Sections: SMS & Wait Lists',
        content: `**SMS Messages Section:**
- **Today's Failed SMS** — Failed SMS jobs today. Options: Cancel, Resend, Resolve
- **Unconfirmed SMS** — SMS sent where patient has not confirmed attendance. Can filter by worksite or service department

**Wait Lists Section:**
- Displays a tile for each configured wait list with current counts`
      },
      {
        title: 'Patients, Actions & Browse',
        content: `**Patients Area:**
- **Duplicate Patients** — Count of potential duplicates with merge screen (requires permissions; do NOT use merge with PMI integration)
- **Attending Patients** — Currently "Arrived" patients with options to arrive, depart, claim, or release
- **Locked Patients** — Unlock with appropriate permissions

**Actions Area:**
- **Appointment Reminders** — Manually generate reminders
- **Manage Distribution Tasks** — Batch Distribute Reports, Export HL7 Batch Reports (useful for re-distributing reports for a specific Requesting Practitioner)
- **Expired Users** — List of expired user profiles with links to expired resources

**Browse Area:**
- **Document Dictionaries** — Manage proposed words (accept, propose, ban)
- **Patients** — Full patient jacket: requests, reports, invoices, observations, forms. All workflow options available
- **Reports** — All reports with content, links to patient/request records, all reporting workflow options
- **Requests** — All requests with links to patient/service/practitioner, management and investigation options
- **SMS Messages** — Cancel, resend, or resolve messages`
      },
      {
        title: 'Configuring Practice Management',
        content: `The Practice Management screen can be customised per user profile.

**Configuration Steps:**
1. Navigate to **Data Management** > search for **Workflow Practice Management Configuration**
2. Edit the configuration:
   - **Disable Counts** — Turn off the count numbers next to each category
   - **Hide Sections** — Turn off specific sections from the screen
   - Select which sub-items within each section to display
3. Assign to a User Profile: **Data Management > User Profiles** > Appearance section > **Workflow practice management configuration**

**Notes:**
- If no configuration is assigned, Practice Management screen is still available but the dashboard is not viewable
- Each user can have a different configuration showing only relevant sections
- Configuration helps reduce visual clutter for users who don't need all sections`
      }
    ]
  },
  {
    id: 'quick-procedures',
    title: 'Quick Procedures',
    subtitle: 'Admin Cheat Sheets',
    icon: 'Settings',
    color: '#6366F1',
    description: 'Step-by-step cheat sheets for common admin tasks: adding users, resources, services, practitioners, charge items, report decks, SMS config, and more.',
    steps: [
      {
        title: 'Adding a New User',
        content: `**Complete process for onboarding a new K4 user:**

1. **Create user in Active Directory** + add to applicable security group (IT assists). Karisma uses AD for passthrough authentication on login.
2. **If Radiographer, Sonographer, Nurse, or Radiologist** — add them as a **Resource** (see Adding a Resource)
3. **If Radiologist** — also add as a **Reporting Practitioner**
4. **After user logs into Karisma:**
   - Main Menu > **Data Management** > **User Profiles**
   - Select the new user > **Edit**
   - Under **Appearance** section > **Workflow View Profile** > click search > select applicable profile
   - Complete any site-specific options per key user requirements
5. Click **Save**`
      },
      {
        title: 'Adding a Resource',
        content: `1. Main Menu > **Resource Management** > Management > **Resources**
2. Click **New**
3. Enter the following:
   - **Code** — Identifier (room number, staff initials, etc.)
   - **Name** — Staff member name or room name
   - **Description** — Job title or location
   - **Resource Type** — e.g., BMD Room, Sonographer, Radiologist
   - **Physical Worksite** — Room/Radiologist: select worksite location. Radiographer/Sonographer: determines scheduling/imaging availability at specific sites
   - **Timeline Scale** — Room/Radiologist: time per diary slot. Radiographer/Sonographer: enter 5 mins (mandatory)
   - **Colour** — If site uses colour filters on diary
   - **Allow resource to be scheduled** — Tick if bookable
4. **Operating Profiles** — If room/scheduled radiologist: click New > create opening/closing hours
5. Click **Save**`
      },
      {
        title: 'Adding a Service Type',
        content: `1. Main Menu > **Service Management** > Management > **Services**
2. Click **New**
3. Enter the following:
   - **Code** — Follow site convention (usually Modality + Body Region + Laterality)
   - **Name** — Appears on all fields within Karisma and downstream systems
   - **Order Rule** — Whether you can schedule and/or invoice the service
   - **Perform Rule** — Requirements for confirmation types, times, labels
   - **Department** — Applicable service department
   - **Image Required** — If ORM message needs to be sent to PACS
   - **Report Required** — If a report is needed
   - **Report Type** — Usually GenRep
   - **Report Type Service** — Usually GenRep
   - **Modality** — Usually Rad
   - **Rebate Service Types** — For billing
   - **Procedure** — Which room/resources needed
4. Click **Save**

**Note:** Additional site-specific options available — confirm with key user.`
      },
      {
        title: 'Adding a Requesting Practitioner',
        content: `1. Main Menu > **Practitioner Management**
2. **Search** under practitioners and location section — ensure not already added
3. Under Actions > **Add New Practitioner**:
   - **Practitioner Code** — Use site's configured code
   - **Title, First Name, Surname**
   - **Phone (M)** — Mobile if available
   - **Practitioner Specialty** — Pick applicable specialties
4. Under Assignment > **Add Assignment**:
   - **Assignment Type** + value (e.g., **Provider Number**)
   - **Location** — From list or add new
   - **Contact Group** — Defines how reports are distributed
5. Add **additional assignments** for each worksite (separate assignment per site)
6. Select the **Star** on main assignment = default
7. Click **Create** > Confirm > **Finish**`
      },
      {
        title: 'Adding a Reporting Practitioner',
        content: `**Prerequisites:** Follow Add New User steps first.

1. Main Menu > **Practitioner Management**
2. Search to ensure practitioner not already added
3. **Add New Practitioner:** Code, Title, First Name, Surname, Specialty
4. **Add Assignment:** Type + Provider Number, Location, Contact Group
5. Additional assignments per worksite; **Star = default**
6. Click **Create**
7. Select **Edit Practitioner**, then:
   - Under Authority: **Invoicing level** = Full (or appropriate option)
   - Tick **"Is reporting provider"**
   - Tick **"Can supervise reporting"** (if applicable)
   - **Associated User** = select the radiologist
   - **Financial Site** = link appropriate financial sites
8. Click **Save** > **Finish**`
      },
      {
        title: 'Creating a Report Deck',
        content: `1. Main Menu > **Data Management** > **Report Decks**
2. Click **New**
3. Enter **Name** and **Caption**; set **Published** if available to all staff
4. Under Sections > **New**:
   - **Caption** — Displayed name on the Report Deck
   - **Workflow Type** — e.g., "Typist"
   - **Claim Type** — "Mine" (assigned to user) or "All" (all reports)
   - **Display Option** — Matrix, Horizontal, or Vertical list
5. **Add Filter:** New (name + filter options + join expressions + Save) or search existing
6. Add **Deck Layout** if defined
7. Click **OK**; move sections up/down as needed
8. Click **Save**
9. **Publish** to users (see next step)`
      },
      {
        title: 'Adding a Report Deck to a User',
        content: `**Method 1 — Direct Assignment:**
1. Main Menu > **Data Management** > **User Profiles**
2. **IMPORTANT:** User must be **logged OUT** of Karisma (warning appears if not)
3. Select user > **Edit**
4. Under **Reporting** section > **Report Decks** > **New** > **Find** > select deck > **OK** > **OK**

**Method 2 — Publish to Multiple Users:**
1. Main Menu > **Data Management** > **Report Decks**
2. Select the deck > **Publish**
3. Choose to publish by **Role**, **Group**, or **User**
4. Ensure required users are selected
5. Click **Publish**`
      },
      {
        title: 'Adding a Custom Charge Item',
        content: `1. Main Menu > **Charge Management** > Management > **Items**
   - Ensure you're filtering to the **current schedule**
2. Click **New**
3. Enter:
   - **Code** — Unique identifier (e.g., USB)
   - **Name** — Name of the charge item
   - **Description** — Further information (e.g., "USB Drive")
   - **Default Amount** — Usually $0 if charge levels apply
   - **Schedule** — Most recent charge schedule
   - **Charge Department** — Internal filtering metric (default: CR)
   - **Service Type** — For private charge items, link to specific service types
4. Click **Save**
5. Select **Levels** on right-hand side > define charge amount per level > **OK**`
      },
      {
        title: 'Adding Item to a Charge Scheme',
        content: `1. Main Menu > **Charge Management** > Management > **Schemes**
   - Ensure filtering to the **current schedule**
2. Select the applicable **charge scheme**
3. Under the Items section on the right > click **Add Item**
4. Search for the charge item using **item code or name**
5. If adding multiple items, use the **tick box** to select (clicking elsewhere unselects others)
6. Select item(s) and click **OK**`
      },
      {
        title: 'Service Preparation Notes',
        content: `**Adding a Note:**
1. Main Menu > **Data Management** > **Service Preparation Notes**
2. Click **New**
3. Enter **Code** (follow site convention), **Name**, and **Notes Content**
4. Click **Save**

**Adding an Instruction (links note to services):**
1. Main Menu > **Data Management** > **Service Preparation Instruction**
2. Click **New**
3. Select the **Note** you created
4. **Link** to a Service Type, Service Step Type, Service Procedure, or Work Site
5. Tick **Appointment Letter** if you want the note printed with appointment letters
6. Select **time period** or **age bracket** if applicable
7. Select **request contexts** (inpatient/outpatient/emergency) if applicable
8. Click **Save**

**Updating:** Data Management > Service Preparation Notes > select > Edit > adjust > Save`
      },
      {
        title: 'SMS Configuration & Templates',
        content: `**Updating SMS Provider Configuration:**
1. Main Menu > **Data Management** > **SMS Provider Configurations**
2. Select the provider configuration > **Edit**
3. Adjust:
   - **Start send time** — When SMS messages start being sent
   - **End send time** — When SMS messages stop being sent
   - **Minimum notification period** — Minimum time between send attempt and appointment
4. Click **Save**
- **Note:** If start and end times are removed, patients receive SMS at 12:01 AM

**Updating SMS Templates:**
1. Main Menu > **Data Management** > **SMS Templates**
2. Select the template > **Edit**
3. Update the message text
4. To add a field, **double-click** on the appropriate field to insert it as a variable
5. Review the summary — **160 character limit** per SMS (exceeding may incur charges)
6. Click **Save**`
      },
      {
        title: 'Service Perform Confirmation Types',
        content: `**Updating confirmation types that appear during service verification:**

1. Main Menu > **Data Management** > **Service Type Perform Rules**
2. Select the applicable perform rule > **Edit**
3. Under **Verification**, click the **Search** button
4. Either:
   - **Select an existing** Service Perform Confirmation Type > **OK**
   - Or click **New** > create a new type with applicable requirements > **Save** > **OK**
5. Click **Save** to apply all updates

**Purpose:** These confirmation types define what questions or checkboxes appear when imaging staff commence or verify a service (e.g., "Patient identity confirmed", "Correct side marked", "Pregnancy status checked").`
      },
      {
        title: 'Ad Hoc Report Distribution',
        content: `**To manually distribute a report to a specific practitioner:**

1. Navigate to the **reception/imaging workflow screen**
2. Search for the patient and select the relevant request
3. Click to **add a recipient**
4. Select the **practitioner assignment** you want to send the report to
5. Choose the **method of distribution** (or default option) and click **OK**
6. Scroll down to the report tile and click the **print icon**
7. The **distribution dialog** opens — click **"referring assignment"** then **"advanced"**
8. The report distribution dialog shows existing automated distribution methods
9. **Untick** any options you do not want
10. Click **OK** to distribute`
      },
      {
        title: 'Recommended Training Videos',
        content: `**Reception Staff (recommended order):**
Karisma Overview > Reception Overview > Using Workflow Views > Search For and Add Patient > Scheduling A Request > Reschedule From Reception > Cancel Service Step > Scanning in Reception > Register Scheduled Request > Register Walk In > Validating Medicare > Adding a CC Recipient > Printing a Label > Note Templates > Adding a Requesting Practitioner > Diary Overview > Schedule from Diary > Scheduling Multiple Services > Reschedule from Diary > Cancel Service Step from Diary > Raising a Private Invoice
**Optional:** Practitioner Management, Accounting Overview, Surcharge Invoice, Practice Management, Distribution Jobs

**Imaging Staff (recommended order):**
Karisma Overview > Imaging Overview > Using Workflow Views > Printing a Label > Scanning in Imaging > Note Templates > Commence and Verify > Adding a Service > Editing a Service > Regress Service Step > Cancel Service Step > Diary Overview > Reschedule from Diary > Cancel Service Step from Diary
**Optional:** Imaging Tablet

**Reporting Staff (recommended order):**
Karisma Overview > Reporting Overview > Using Report Templates > Dictation > Transcription > Preliminary > Authorisation > Amendment > Creating Report Templates > Report Search > Diary Overview

**Additional:** Approvals, Online Bookings, Request Search, Transport, Wait Lists
**My Health Record:** Webinar, Patient IHI Validation, Practitioner HPI-I Validation, My Health Record Workflow (via Zendesk)`
      }
    ]
  },
  {
    id: 'wait-list',
    title: 'Wait List Module',
    subtitle: 'Organise & Action Requests',
    icon: 'Clock',
    color: '#0284C7',
    description: 'Organise and action Received or Scheduled Requests with long lead times or priority needs. Configure wait lists, filters, grouping, and control who can view and manage them. Based on Kestral Wait List Module Guide v4 (16/04/2026).',
    steps: [
      {
        title: 'Wait List Overview',
        content: `The **Wait List Module** in Karisma facilitates the organisation and actioning of Requests. It provides staff with a way to filter lists of **Received** and/or **Scheduled** Requests that meet a set of criteria for their workflow.

**Common uses:**
- As a wait list for an imaging service with a 3-6 month lead time
- To prioritise work before it is scheduled
- To manage the list of Inpatient Requests to work through on a day

**Example workflow in detail:**
1. A Request is Received into Karisma
2. The Request goes through the Approval process and is allocated a Service Completion Priority of **Routine** (needs to be scheduled in 3-6 months)
3. The Approval process is completed
4. The Request appears in the Wait List Module, based on the Wait List Filter
5. In 3-6 months, the Request is accessed through the Wait List Module and Scheduled
6. The Request drops off the Wait List Grid

**Kestral Guide reference:** Wait List Module, Version 4 (16/04/2026)`
      },
      {
        title: 'Accessing the Wait List Module',
        content: `**To access the Wait List Module:**
1. In the **Navigation Bar**, click the **Menu** button
2. Click the **Wait Lists** button (clock icon)

**Note:** Access to the Wait List Module is controlled via a Security Permission — **Operate Wait List Workflows**.`
      },
      {
        title: 'Screen Overview',
        content: `The Wait List screen is composed of the following areas:

| Area | Description |
|------|-------------|
| **Wait List Types** | Selector (top-left) showing all configured wait lists for the Work Site. Users switch between lists here. |
| **Patient Search** | Search box — filters the Wait List Grid by patient name, patient identifier, or date of birth. |
| **Wait List Grid** | Grid layout showing Patient, Services, Imaged By date etc. Highlighted row = current selection. **Pill** icon = patient Allergies. **Triangle** icon = patient Conditions. Certain columns are sortable. |
| **Preview Panel** | Extra info for the selected row. If Transport is enabled, jobs can be routed from here. |
| **View In** | Opens the selected row in **Reception**, **Imaging** or **Approval** screens in-context. |
| **Action Bar** | Perform actions against the selected row. |
| **Count** | Shows the number of Requests currently on the grid (max 2000 visible). |

**Note:** A **2-minute timer is hardcoded** to reload the Wait List screen.`
      },
      {
        title: 'Wait List Types Selector',
        content: `Clicking the **Wait List Types** option (top-left) opens a list of all wait lists created for the Work Site you are logged into. Selecting a list updates the grid with applicable Requests.

- A **search bar** lets you find a particular list quickly
- The **Show All** toggle makes wait lists across **every Work Site** visible. When off, you only see wait lists whose configured Work Site matches your **active Work Site**
- The **Management** button opens the Wait List configuration screen

**Note:** Access to the Wait List Management screen is controlled via a Security Permission (see the Security Permissions step).`
      },
      {
        title: 'Preview Panel — Overview tab',
        content: `The Preview Panel is split into three tabs: **Overview**, **Forms**, **Approval**.

**Overview tab** gives access to a mix of the Patient Jacket and Request Jacket, including:
- Patient Toolbar
- Request Toolbar
- Action buttons to update and edit both Patient details and Request details directly from the Wait List`
      },
      {
        title: 'Preview Panel — Forms tab',
        content: `The **Forms** tab displays all Forms scanned for the selected Request.

From this panel you can:
- Print Forms
- Save Forms
- Delete Forms
- **Pop out** a Form into a new window (useful for moving to a second monitor)`
      },
      {
        title: 'Preview Panel — Approval tab',
        content: `The **Approval** tab provides:
- A summary of the Approval
- Who is currently assigned
- The Approval history
- The ability to **reactivate** the Approval if required`
      },
      {
        title: 'Action Bar',
        content: `The **Action Bar** allows users to perform one of the following actions against the selected row:
- **Schedule** the Request
- **Register** the Request
- **Print Request Labels**
- **Scan Forms** to the Request

**Note:** Once a Wait List has been created, it can be added to users' **Practice Management Dashboards**. See the Practice Management Manual for details.`
      },
      {
        title: 'Configuration — Wait List fields',
        content: `To configure a Wait List, select **Wait List Types > Management**. Two sections are available: **Waitlists** and **Filters** (also reachable via Data Management).

**Wait List fields:**

| Field | Description |
|-------|-------------|
| **Name** | Displayed in the Wait List Types selector. |
| **Worksite** | The Work Site(s) the Wait List is created for. |
| **Priority** | Priority type shown on the Wait List worklist. |
| **Condition Group** | Request Condition Group available for grouping the grid. The **Mutually Exclusive** flag must be defined. |
| **Note Style** | Determines which Request Notes appear on the grid. |
| **Transport Region** | Which Work Sites are visible when routing Transport Jobs from the Overview panel. |
| **Filter** | Extra criteria (Service Type, Service Department, Request Context, etc.) to refine which Requests are visible. See Filters step. |
| **Order** | The Wait List Module returns the top 2000 results. This field drives the priority of returned Requests — options: **Imaged By date**, **Received Date**, **Priority Type**. |
| **Grouping** | None · **Priority** (uses Grouping Priority field) · **Condition Group** (uses Condition Group field). |
| **Grouping Priority** | Alternative Priority Type used for grouping (different from the Priority column). |`
      },
      {
        title: 'Configuration — Option checkboxes',
        content: `The following tick-box Options are available on the Wait List configuration:

| Option | Description |
|--------|-------------|
| **Requires Completed Approval** | Only Requests with a completed Approval will appear on the Wait List. |
| **Include Scheduled Requests** | Scheduled Requests will also appear on the Wait List. |
| **Show Host Location** | Where Karisma receives a PMI/ADT feed, the Ward / Room / Bed will display on the Wait List (common in hospital settings). |
| **Is Discarded** | Marks the selected Wait List as discarded. |`
      },
      {
        title: 'Filters',
        content: `The **Filter List** lets you create a reusable set of criteria that determines what appears on a Wait List. Filters can be used across multiple Wait Lists.

Filter areas include: **DETAILS**, **REQUESTS**, **ENTITY GROUPS**, and **DATE**.

**Date column** — sets a time period the Wait List is effective for, based on:
- **Received Date**
- **Imaged By Date**
- **Scheduled Date**

Date options must be defined in **Data Management** before they can be selected on a Wait List Filter:
- **Received Date** and **Scheduled Date** → defined under **Filter Date Time Options**
- **Imaged By Date** → defined under **Filter Date options**

Dates can be defined by **Set Types** (e.g. today, after today, next month, this week), refined relatively ("X days before today"), or customised to a specific date range.`
      },
      {
        title: 'Security Permissions',
        content: `Two permissions govern access to the Wait List Module:

- **Operate Wait List Workflows** — enables a user to access and use the Wait List Module
- **Manage Wait List Configuration** — enables a user to configure Wait Lists and Filters via the Management screen`
      }
    ]
  },
  {
    id: 'sp-workflows',
    title: 'SP Patient Journey',
    subtitle: 'Booking → Reception → Imaging → Reporting → Billing',
    icon: 'Route',
    color: '#CA8A04',
    description: 'SP Radiology patient journey workflows within Karisma: booking, reception (arrival + departure), imaging, reporting via external VR software, and billing per account type. Based on the SP Workflow Documentation v1.1 (10/01/2024).',
    steps: [
      {
        title: 'Overview & The Five Workflows',
        content: `The **SP Workflow Documentation** maps the generic patient and request journey within Karisma at **Sanctuary Point Ultrasound & Radiology**.

**Five workflow stages:**
1. **Booking** — manage the referral
2. **Reception** — patient arrives
3. **Imaging** — image the patient
4. **Reception** — patient leaves (invoice)
5. **Reporting** — report on the patient's images

Plus a cross-cutting **Billing** summary covering every account type.

> These are generic templates and should be customised by SP to match your specific operational needs. Review each workflow and update if any discrepancies are found.

**Source:** SP Workflow Documentation v1.1 (10/01/2024) — prepared by Danielle Barnett, Sanctuary Point and Interventions.`
      },
      {
        title: 'Booking — Receive vs Schedule',
        content: `Two distinct actions underpin the booking workflow:

| Action | Description |
|--------|-------------|
| **Receive** | Stores a patient request in Karisma **before** it is scheduled or registered. |
| **Schedule** | Allocates a **date, time and room** to the request. |

> ⚠️ A request does **not** need to be Received before it is Scheduled — Schedule can be the **first step**.

**Patient communication rules at SP Radiology:**
- **SMS Reminders** sent **1 day** before the appointment
- **SMS Confirmations** sent with a **3-hour threshold**
- **Appointment Letters** are set for **manual distribution**`
      },
      {
        title: 'Booking — Workflow by Imaging Type',
        content: `The booking path diverges by imaging type:
- BMD / OPG
- Non-Contrast CT & MRI
- US / MG
- Contrast CT & MRI

**For each imaging type, the standard Receive sub-processes are:**
1. Schedule in Reception
2. Scan in the Request Form using **Scan**
3. Import the digital Request Form using **Forms**
4. Add the **RFT** (Request For Tracking) Request Condition to the Request
5. Import the digital Request Form using **Forms**

**Scheduling path via Waitlists:**
- Schedule in Waitlists using the **Received List**
- Schedule in Waitlists using the **RFT List**`
      },
      {
        title: 'Booking — XR Walk-in Only',
        content: `**XR Walk-ins have no scheduling workflow.** The request is handled entirely at reception when the patient arrives:

1. Scan in the Request Form using **Scan**
2. Import the digital Request Form using **Forms**
3. Add the **RFT Request Condition** to the Request
4. Import the digital Request Form using **Forms**

Registration happens immediately on patient arrival (see the Reception step).`
      },
      {
        title: 'Booking — Cancellations',
        content: `The **Cancellation Request Condition** can be applied during scheduling to indicate the patient is **available for an earlier appointment** if one becomes available.

This links the request to the **Cancellation List** in Waitlists. When a slot opens, reception can proactively phone/SMS the patient from this list.`
      },
      {
        title: 'Reception — Patient Arrives (Registration)',
        content: `When a patient arrives for imaging:

**Step 1 — Always:**
- Validate Medicare Card
- Check patient details and confirm booking/walk-in

**Step 2 — Branch on scenario:**

**Branch A — Booking:**
- **CT / MRI / US / MG:** Scan any additional forms using **Forms** → **Register**
- **XR / BMD / OPG:** → **Register**

**Branch B — Walk-in:**
- Schedule Request → **Register** → Scan all forms using **Forms / Scan**

> **Register** acknowledges the patient has arrived in the practice. A request does NOT need to be received or scheduled first — Register can be used as the first step for walk-ins.

**Scheduling note:** The Doctor resource is organised by **Rosters** for worksite availability.

**Post-Golive:** MRI service preparation notes and forms to be added.`
      },
      {
        title: 'Imaging — Commence, Complete, Verify',
        content: `Once the patient is Registered, they appear in the **Waiting Room**.

**Opening steps (all modalities):**
1. Collect the patient
2. Confirm patient and service details

**Then branch on modality:**

**MRI / CT / US / MG:**
1. **Commence** Service (tech has started the procedure)
2. **Complete** Service (patient has finished imaging, but extra tech work remains — post-processing or worksheets)
3. **Verify** Service (images are on PACS, ready to be reported)
4. Scan any worksheets using **Forms**
5. **Verify** Service again

**XR / BMD / OPG:**
- Single **Verify** Service step

| Stage | Meaning |
|-------|---------|
| **Commence** | Tech has started the procedure. |
| **Complete** | Patient has finished imaging but extra steps remain (post-processing, worksheets). |
| **Verify** | Images are on PACS and ready to be reported. |

**Post-Golive:** MRI services and resource to be added.`
      },
      {
        title: 'Reporting — External VR Software',
        content: `The reporting workflow is triggered when **all services within a Request have been Verified**.

**Steps:**
1. Select the request and click **Dictate**
2. In the **External VR Software**: complete the report using VR
3. Copy the report contents back into **Dialog** and review
4. **Authorise** the Report

⏱ **Delayed Authorisation** is set to **15 minutes** — giving the reporter a window to recall and amend before the report locks.

> Once Authorised, the report is **finalised and ready for distribution**. Reports are **automatically distributed** to the referring practitioner and any recipients that meet the distribution requirements.`
      },
      {
        title: 'Reception — Patient Leaves (Invoicing)',
        content: `After imaging is complete, the invoicing workflow branches by **Account Type**:

**Branch A — Bulk Bill / DVA / Workcover:**
1. Invoice Request
2. Accounting staff to Process

**Branch B — Private / Pensioner / Overseas:**
1. Invoice Request
2. Process Payment
3. **Private & Pensioner only:** Process Claim

> **Invoiced** = the request has met the requirements to allow the patient to be billed.

**Invoices are set to manual distribution.**

**Optional — Adjusting Invoices:**
Invoices can be adjusted through the invoice's icon in reception — adjust charge amounts, distribute a new invoice, receive payment, and distribute a receipt.`
      },
      {
        title: 'Billing — Account Types Summary',
        content: `> ⚠️ **Both sites are clinically and financially segregated.**

| Account Type | Invoiced | Paid |
|--------------|----------|------|
| **M** Medicare Bulk Bill | After Imaging | Invoice Batch — Claim via Medicare Online |
| **V** Veterans Affairs | After Imaging | Invoice Batch — Claim via Medicare Online |
| **P** Private Medicare | After Imaging | Upfront + Medicare Claim |
| **PEN** Pensioner | After Imaging | Upfront + Medicare Claim |
| **O** Overseas | After Imaging | Up Front |
| **W** Workcover | After **Reporting** — all invoices issued via the **Debtor Page** | Manual payments received via the Debtors Page |

Note how **Workcover is the only account type** where invoicing happens after Reporting, not Imaging — and where payments are tracked and received manually through the Debtors Page rather than via Medicare Online.`
      },
      {
        title: 'Billing — Medicare Online Processing',
        content: `**Medicare payments are distributed at 12:00 AM** each day.

### Best practice

> **'Manage and Process' any batches first thing in the morning.**

This ensures yesterday's payments are reconciled before today's workflow begins — preventing account discrepancies and keeping the Debtors Page clean for accounting staff.`
      }
    ]
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    subtitle: 'Recovery Workflows & SOPs',
    icon: 'Wrench',
    color: '#BE123C',
    description: 'Diagnose and resolve common K4 issues: missing patients, lost requests, billing problems, report queue issues, and multi-site confusion.',
    steps: [
      {
        title: 'First Principles for Troubleshooting',
        content: `When something "goes wrong" in K4, the safest assumption is:

**The system is usually reflecting a status, rule, filter, or site-context problem before it is reflecting a true software fault.**

K4 is designed around multiple linked modules. Each stage depends on the prior stage being completed correctly.

**Core Troubleshooting Sequence:**
1. Confirm **worksite** — are you logged into the correct site?
2. Confirm **user profile / permissions** — does your profile allow this action?
3. Confirm **request status** — is the request in the expected state?
4. Confirm **service / report / invoice status** — check downstream statuses
5. Confirm **workflow screen filter or search filter** — is your view filtering it out?
6. Only then assume configuration or software fault

**Safest Debugging Tools:**
- **Request Search** — Search by ID, demographics, notes. Open in Reception/Imaging/Accounting.
- **Report Search** — Keyword/patient search, view/edit/dictate/distribute actions.
- **Workflow Views** — Check filters, sorting, highlights applied to your screen.
- **User Profiles** — Check appearance, behaviour, and reporting configuration.`
      },
      {
        title: 'Recovery: Patient Cannot Be Found',
        content: `**Likely Causes:**
- Patient searched poorly (too specific or wrong field)
- Duplicate patient exists under different details
- Expired identifier not searchable
- Wrong site context or workflow assumptions
- PMI / external source logic if integrated

**Safe Recovery Process:**
1. Search by **surname/first name fragment**
2. Search by **date of birth**
3. Search by **combination of name + DOB**
4. Search by **Medicare / MRN / other identifier**
5. If still not found, check whether your user profile allows searching expired identifiers
6. Try **Practice Management patient search** rather than assuming a new patient is needed
7. Only create a new patient after reviewing plausible duplicates

**HARD RULE:** Do NOT create a new patient record just because the first quick search failed. Karisma warns about similar records and phonetically similar names — review these warnings carefully.`
      },
      {
        title: 'Recovery: Request Not on Expected Worklist',
        content: `**Likely Causes:**
- Wrong request status (Received vs Scheduled vs Registered vs Cancelled)
- Wrong service date / scheduled date logic
- Worklist filter mismatch
- Worksite-specific workflow configuration
- User is on the wrong worksite

**Safe Recovery Process:**
1. Open the request in **Request Search** — this is your most powerful recovery tool
2. Check request status: Received, Scheduled, Registered, or Cancelled
3. Check workflow dates: scheduled date, service date, received date
4. Check your current workflow screen filter / workflow view
5. Confirm your logged-in worksite

**Common Pattern:** A request is often "missing" because it is in the wrong status for the target worklist, not because it has disappeared.

**Important:** The Default Service Date time option must be set to **"defaulted"**, otherwise Imaging screen worklists will not populate.`
      },
      {
        title: 'Recovery: Billing/Invoice Missing',
        content: `**Likely Causes:**
- Account type missing or wrong
- Pricing-required fields incomplete
- Workflow config hides invoice action
- Request suppressed for invoicing
- Financial period missing
- Wrong worksite / financial site

**Safe Recovery Process:**
1. Open request details and confirm: **account type**, **worksite**, **financial site**, **suppress invoicing** not set
2. Confirm the request has reached the correct status for invoicing
3. Confirm your workflow screen exposes invoice actions
4. Confirm **financial periods** exist — lack of a financial period can completely stop invoicing
5. Escalate to key user for invoice rule / charge scheme review if front-end fields are correct

**Tip:** Reception configuration separately controls whether the Invoice action is displayed and whether "Awaiting Billing" is used. Check both.`
      },
      {
        title: 'Recovery: Report Not in Queue',
        content: `**Likely Causes:**
- Service not yet verified (imaging must verify before reporting)
- Report deck / user profile problem
- Workflow filter problem
- Wrong worksite
- Report exists but clinical availability/status is not what you expect

**Safe Recovery Process:**
1. Use **Request Search** to confirm the request and service exist
2. Confirm **service verification status** — has imaging completed verification?
3. Use **Report Search** by patient, requesting practitioner, service, date, or keyword
4. Confirm your **user profile** has the correct report decks and reporting settings
5. Confirm your logged-in worksite

**Important Distinction:** A study can exist clinically but still not be eligible for your reporting screen if:
- It is not verified
- It is outside your reporting deck
- Your profile is misconfigured`
      },
      {
        title: 'Recovery: Cannot Edit/Dictate/Distribute Report',
        content: `**Likely Causes:**
- Report is in a different availability or process state
- Permissions are insufficient
- Wrong action selected
- User profile or workflow role mismatch

**Safe Recovery Process:**
1. Open the report via **Report Search**
2. Confirm whether you can **view** but not **edit** — this distinguishes permission from state issues
3. Check whether the problem is: reporting state, permissions, or user profile
4. For persistent issues, compare with another reporting user profile that does have access

**Report Search Actions Available:**
- View report
- Edit report
- Dictate report
- Distribute report
- Export text`
      },
      {
        title: 'Recovery: Template Issues',
        content: `**Likely Causes:**
- Wrong template code
- Department/category restrictions blocking the template
- User access restriction
- Unsupported field type for voice recognition
- Voice name missing for VR insertion

**Safe Recovery Process:**
1. Confirm the correct **template code** exists in Data Management > Report Templates
2. Confirm **department/category restrictions** — template may be limited to specific departments
3. Confirm whether the template is **available to all users** or restricted to specific security users
4. If using Voice Recognition:
   - Confirm template has a **voice name** assigned
   - Confirm target input fields are **Text** or **Paragraph** type (only VR-compatible types)
5. Test **manual insertion** first (Ctrl+Space), then test VR insertion

**Key Rule:** Only **Text** and **Paragraph** input field types are VR-compatible. Boolean, Number, Integer, Date, and List fields cannot be dictated into.`
      },
      {
        title: 'Recovery: Missing Workflow Views/Filters',
        content: `**Likely Causes:**
- Workflow view profile not published
- Search view not published
- No client restart after publication
- Insufficient permissions

**Safe Recovery Process:**
1. Confirm the **search view** exists in Data Management > Search Views
2. Confirm the **workflow view profile** includes the search view
3. Confirm the profile was **published** to the correct user/role/group
4. **Restart the Karisma client** — changes only take effect after restart
5. Confirm permissions:
   - **Search request records / Search report content** — to run shared filters
   - **Manage Search Filters** — to create personal filters
   - **Data Management** — to create/publish search views

**Common Pattern:** The most frequent cause of "missing" views is simply that the user has not restarted Karisma after a profile was published.`
      },
      {
        title: 'Recovery: Duplicate Practitioner Issues',
        content: `**Likely Causes:**
- Practitioner record created twice accidentally
- Location or assignment mismatch
- Old practitioner retained after merge
- Requests still linked to expired practitioner records

**Safe Recovery Process:**
1. **Search** practitioner before creating a new one — always
2. Use **Practitioner Management** rather than ad hoc edits
3. If duplicate exists, review **merge candidates**
4. During merge, decide whether to **update existing request assignments** to the merged practitioner

**HARD RULE:** Do NOT create another slightly different practitioner record to work around a duplicate. This compounds the problem and creates downstream billing and reporting issues.`
      },
      {
        title: 'Multi-Site Confusion',
        content: `**Common Symptoms:**
- Patient seems to have incomplete history
- Wrong billing site
- Staff seeing wrong work queue
- Scanner or workstation defaults seem wrong

**Safe Recovery Process:**
1. Confirm current **logged-in worksite**
2. Confirm **workstation default settings** (Data Management > Workstations)
3. Determine if the issue is:
   - **Visibility** — can you see data from the other site?
   - **Clinical segregation** — are records clinically separated?
   - **Financial segregation** — are records financially separated?
4. Use **Request Search** rather than relying only on workflow screens
5. Escalate to key user before changing workstation defaults globally

**Tip:** Check the "signed in as" information in the top right corner to confirm your current worksite context.`
      },
      {
        title: 'SOP: Reception Safety Checklist',
        content: `Before registering any request, complete this checklist:

1. **Verify patient identity** using at least two identifiers (name + DOB)
2. **Search thoroughly** — do not create a new patient without extensive search
3. **Verify account type** — this determines all downstream billing
4. **Verify requesting practitioner** — confirm correct referrer
5. **Verify worksite / financial site context** — are you on the right site?
6. **Confirm referral** is present or document why it is absent

**Red Flags — Stop and Investigate:**
- Creating patient without adequate search
- Registering request without account type
- Wrong site context selected
- Using narrow workflow filters that might hide the patient
- Scanning step skipped when referral is present`
      },
      {
        title: 'SOP: Imaging Safety Checklist',
        content: `Before verifying any service, complete this checklist:

1. **Confirm service type** is correct for the examination performed
2. **Confirm side/site details** — left, right, bilateral as appropriate
3. **Confirm imaging notes** are entered if required
4. **Confirm worksheets/forms** have been handled (scanned, filed)
5. **Confirm no further imaging step** is pending before verification

**HARD RULE:** Do NOT verify early just to clear the worklist. Verification is the handoff point to both reporting and billing. Once verified:
- The study becomes eligible for the reporting queue
- Invoicing can proceed
- Reversing is more complex and may require key user intervention`
      },
      {
        title: 'SOP: Reporting Safety Checklist',
        content: `Before authorising any report, confirm:

1. **Correct patient and study** — double-check patient name, DOB, and service
2. **Report answers the clinical question** — review the referral reason
3. **Impression hierarchy is clear** — most important findings first
4. **Critical communication documented** — if there are urgent findings, ensure appropriate communication

**Governance Note:** Templates should prioritise clinical communication and remain adaptable to the patient context. They should support quality and interoperability, not become rigid checkbox systems. The reporting radiologist retains override authority and clinical responsibility.`
      },
      {
        title: 'When to Escalate',
        content: `**Escalate to Key User when:**
- Duplicate patients are suspected
- Duplicate practitioners are suspected
- Worksite / financial site mappings seem wrong
- Invoice logic looks wrong across multiple cases
- Workflow views / search views affect multiple staff
- Rebate update testing is required
- A service conflict rule is producing inappropriate warnings
- Client restart or security refresh is required after config changes

**Escalate to Kestral Support when:**
- **P1 Critical** — Server crashes, system inaccessible, major module unavailable
- **P2 Urgent** — Severely affects system, no workaround available
- True workflow-blocking issues without practical workaround
- Enhancement or config issues beyond local authority

**Do NOT escalate to Kestral for:**
- Normal filter mistakes
- Wrong workflow view selection
- Missing published profile after no client restart
- Duplicates caused by local user error (handle internally first)`
      }
    ]
  }
];

export const getModuleIcon = (iconName) => {
  const icons = { Home, Users, Calendar, ClipboardList, Activity, FileText, Search, FileSearch, LayoutGrid, DollarSign, UserCog, AlertTriangle, Shield, ChevronRight, Monitor, Settings, Printer, BookOpen, Wrench, Clock, Route };
  return icons[iconName] || Home;
};
